import React, { useEffect, useState } from 'react';
import { createClient } from '@supabase/supabase-js';
import './App.css';
import { jsPDF } from 'jspdf';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_KEY;
const supabase = createClient(supabaseUrl, supabaseKey);

function App() {
  const [screen, setScreen] = useState('dashboard');
  const [reportPeriod, setReportPeriod] = useState('month');
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [recoveryMode, setRecoveryMode] = useState(false);
  const [recoveryEmailSent, setRecoveryEmailSent] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [passwordMessage, setPasswordMessage] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Estado para dados
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [cash, setCash] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [inventorySearch, setInventorySearch] = useState('');
  const [showInventoryForm, setShowInventoryForm] = useState(false);
  const [editingInventoryId, setEditingInventoryId] = useState(null);
  const [inventoryForm, setInventoryForm] = useState({
    name: '', quantity: '', min_quantity: '', cost_price: '', sale_price: '', unit: 'un'
  });
  const [inventoryMovements, setInventoryMovements] = useState([]);
  const [showInventoryMovementForm, setShowInventoryMovementForm] = useState(false);
  const [inventoryMovementForm, setInventoryMovementForm] = useState({
    inventory_id: '',
    movement_type: 'sale',
    quantity: 1,
    unit_price: '',
    payment_method: 'pix',
    notes: ''
  });
  const [showCashForm, setShowCashForm] = useState(false);
  const [cashFilter, setCashFilter] = useState('month');
  const [cashSearch, setCashSearch] = useState('');
  const [cashForm, setCashForm] = useState({
    type: 'income',
    description: '',
    amount: '',
    payment_method: 'pix',
    category: 'Serviço',
    date: new Date().toISOString().split('T')[0],
    notes: ''
  });
  const [showAppointmentForm, setShowAppointmentForm] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [editingAppointmentId, setEditingAppointmentId] = useState(null);
  const [clientSearch, setClientSearch] = useState('');
  const [showClientForm, setShowClientForm] = useState(false);
  const [editingClientId, setEditingClientId] = useState(null);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [serviceSearch, setServiceSearch] = useState('');
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [editingServiceId, setEditingServiceId] = useState(null);
  const [serviceForm, setServiceForm] = useState({
    name: '',
    price: '',
    duration_minutes: 30,
    active: true
  });
  const [clientForm, setClientForm] = useState({
    name: '',
    phone: '',
    email: '',
    notes: '',
    birth_date: '',
    hair_system_model: '',
    hair_color: '',
    base_size: '',
    hair_type: '',
    installation_date: '',
    last_maintenance_date: '',
    next_maintenance_date: '',
    maintenance_interval_days: '',
    technical_notes: ''
  });
  const [appointmentForm, setAppointmentForm] = useState({
    client_id: '',
    service_id: '',
    appointment_date: new Date().toISOString().split('T')[0],
    start_time: '',
    notes: ''
  });
  const [pendingMaintenanceClient, setPendingMaintenanceClient] = useState(null);
  const [showAgendaCalendar, setShowAgendaCalendar] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));

  // Autenticação
  useEffect(() => {
    const init = async () => {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      setSession(currentSession);
      setAuthReady(true);
      if (currentSession) await loadData();
      else setLoading(false);
    };
    init();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, newSession) => {
      setSession(newSession);

      // Quando o usuário abre o link enviado pelo Supabase para redefinir a senha,
      // o evento PASSWORD_RECOVERY é disparado e mostramos a tela de nova senha.
      if (event === 'PASSWORD_RECOVERY') {
        setRecoveryMode(true);
        setPasswordMessage('');
        setLoading(false);
        setAuthReady(true);
        return;
      }

      if (!newSession) {
        setClients([]); setServices([]); setAppointments([]);
        setCash([]); setScreen('dashboard');
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoginError('');
    setLoginLoading(true);
    const { data, error } = await supabase.auth.signInWithPassword({
      email: email.trim(), password
    });
    if (error) {
      setLoginError('E-mail ou senha inválidos.');
      setLoginLoading(false);
      return;
    }
    setSession(data.session);
    await loadData();
    setLoginLoading(false);
  };

  const handleForgotPassword = async () => {
    const normalizedEmail = email.trim();
    setLoginError('');
    setRecoveryEmailSent(false);

    if (!normalizedEmail) {
      setLoginError('Digite seu e-mail acima para receber o link de recuperação.');
      return;
    }

    setPasswordLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(normalizedEmail, {
      redirectTo: window.location.origin
    });
    setPasswordLoading(false);

    if (error) {
      setLoginError(`Não foi possível enviar o e-mail de recuperação: ${error.message}`);
      return;
    }

    setRecoveryEmailSent(true);
  };

  const handleUpdatePassword = async (event) => {
    event.preventDefault();
    setPasswordMessage('');

    if (newPassword.length < 6) {
      setPasswordMessage('A nova senha precisa ter pelo menos 6 caracteres.');
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setPasswordMessage('As duas senhas não são iguais.');
      return;
    }

    setPasswordLoading(true);
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    setPasswordLoading(false);

    if (error) {
      setPasswordMessage(`Não foi possível alterar a senha: ${error.message}`);
      return;
    }

    setPasswordMessage('Senha alterada com sucesso. Entrando no sistema...');
    setRecoveryMode(false);
    setNewPassword('');
    setConfirmNewPassword('');
    await loadData();
    window.history.replaceState({}, document.title, window.location.pathname);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [clientsData, servicesData, appointmentsData, cashData, inventoryData, inventoryMovementsData] = await Promise.all([
        supabase.from('clients').select('*').order('created_at', { ascending: false }),
        supabase.from('services').select('*').order('created_at', { ascending: false }),
        supabase.from('appointments').select('*').order('appointment_date', { ascending: true }).order('start_time', { ascending: true }),
        supabase.from('cash').select('*').order('date', { ascending: false }),
        supabase.from('inventory').select('*').order('name', { ascending: true }),
        supabase.from('inventory_movements').select('*').order('created_at', { ascending: false }),
      ]);

      if (clientsData.data) setClients(clientsData.data);
      if (servicesData.data) setServices(servicesData.data);
      if (appointmentsData.data) setAppointments(appointmentsData.data);
      if (cashData.data) setCash(cashData.data);
      if (inventoryData.data) setInventory(inventoryData.data);
      if (inventoryMovementsData.data) setInventoryMovements(inventoryMovementsData.data);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    }
    setLoading(false);
  };

  // Clientes
  const resetClientForm = () => {
    setClientForm({
      name: '', phone: '', email: '', notes: '',
      birth_date: '', hair_system_model: '', hair_color: '', base_size: '', hair_type: '',
      installation_date: '', last_maintenance_date: '', next_maintenance_date: '',
      maintenance_interval_days: '', technical_notes: ''
    });
    setEditingClientId(null);
  };

  const openNewClient = () => {
    resetClientForm();
    setShowClientForm(true);
    setSelectedClientId(null);
  };

  const openEditClient = (client) => {
    setClientForm({
      name: client.name || '',
      phone: client.phone || '',
      email: client.email || '',
      notes: client.notes || '',
      birth_date: client.birth_date || '',
      hair_system_model: client.hair_system_model || '',
      hair_color: client.hair_color || '',
      base_size: client.base_size || '',
      hair_type: client.hair_type || '',
      installation_date: client.installation_date || '',
      last_maintenance_date: client.last_maintenance_date || '',
      next_maintenance_date: client.next_maintenance_date || '',
      maintenance_interval_days: client.maintenance_interval_days || '',
      technical_notes: client.technical_notes || ''
    });
    setEditingClientId(client.id);
    setShowClientForm(true);
    setSelectedClientId(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const saveClient = async (event) => {
    event.preventDefault();

    if (!clientForm.name.trim()) {
      alert('Informe o nome do cliente.');
      return;
    }

    const payload = {
      name: clientForm.name.trim(),
      phone: clientForm.phone.trim(),
      email: clientForm.email.trim(),
      notes: clientForm.notes.trim(),
      birth_date: clientForm.birth_date || null,
      hair_system_model: clientForm.hair_system_model.trim(),
      hair_color: clientForm.hair_color.trim(),
      base_size: clientForm.base_size.trim(),
      hair_type: clientForm.hair_type.trim(),
      installation_date: clientForm.installation_date || null,
      last_maintenance_date: clientForm.last_maintenance_date || null,
      next_maintenance_date: clientForm.next_maintenance_date || null,
      maintenance_interval_days: clientForm.maintenance_interval_days ? Number(clientForm.maintenance_interval_days) : null,
      technical_notes: clientForm.technical_notes.trim()
    };

    if (editingClientId) {
      const { data, error } = await supabase
        .from('clients')
        .update(payload)
        .eq('id', editingClientId)
        .select();

      if (error) {
        alert(`Erro ao atualizar cliente: ${error.message}`);
        return;
      }

      if (data?.[0]) {
        setClients(prev => prev.map(c => c.id === editingClientId ? data[0] : c));
      }
    } else {
      const { data, error } = await supabase
        .from('clients')
        .insert(payload)
        .select();

      if (error) {
        alert(`Erro ao cadastrar cliente: ${error.message}`);
        return;
      }

      if (data?.[0]) setClients(prev => [data[0], ...prev]);
    }

    resetClientForm();
    setShowClientForm(false);
  };

  const deleteClient = async (id) => {
    const clientAppointments = appointments.filter(a => a.client_id === id);
    if (clientAppointments.length > 0) {
      alert('Este cliente possui agendamentos no histórico e não pode ser excluído. Você ainda pode editar os dados dele.');
      return;
    }

    if (!window.confirm('Excluir este cliente?')) return;

    const { error } = await supabase.from('clients').delete().eq('id', id);
    if (error) {
      alert(`Não foi possível excluir o cliente: ${error.message}`);
      return;
    }

    setClients(prev => prev.filter(c => c.id !== id));
    if (selectedClientId === id) setSelectedClientId(null);
  };

  const openClientAppointment = (client) => {
    setSelectedDate(today);
    setAppointmentForm({
      client_id: String(client.id),
      service_id: '',
      appointment_date: today,
      start_time: '',
      notes: ''
    });
    setEditingAppointmentId(null);
    setShowAppointmentForm(true);
    setScreen('appointments');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const filteredClients = clients.filter(client => {
    const term = clientSearch.trim().toLowerCase();
    if (!term) return true;
    return [client.name, client.phone, client.email]
      .filter(Boolean)
      .some(value => String(value).toLowerCase().includes(term));
  });

  const getClientStats = (clientId) => {
    const history = appointments
      .filter(a => a.client_id === clientId && a.status !== 'cancelled')
      .sort((a, b) => `${b.appointment_date} ${b.start_time || ''}`.localeCompare(`${a.appointment_date} ${a.start_time || ''}`));

    const completed = history.filter(a => a.status === 'completed');
    const totalSpent = completed.reduce((sum, apt) => {
      const service = services.find(s => s.id === apt.service_id);
      return sum + Number(service?.price || 0);
    }, 0);

    return {
      history,
      completedCount: completed.length,
      totalSpent,
      lastVisit: completed[0]?.appointment_date || history[0]?.appointment_date || null
    };
  };

  // Serviços 2.0
  const resetServiceForm = () => {
    setServiceForm({ name: '', price: '', duration_minutes: 30, active: true });
    setEditingServiceId(null);
  };

  const openNewService = () => {
    resetServiceForm();
    setShowServiceForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const openEditService = (service) => {
    setServiceForm({
      name: service.name || '',
      price: service.price ?? '',
      duration_minutes: service.duration_minutes || 30,
      active: service.active !== false
    });
    setEditingServiceId(service.id);
    setShowServiceForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const saveService = async (event) => {
    event.preventDefault();

    const price = Number(String(serviceForm.price).replace(',', '.'));
    const duration = Number(serviceForm.duration_minutes);

    if (!serviceForm.name.trim()) {
      alert('Informe o nome do serviço.');
      return;
    }
    if (Number.isNaN(price) || price < 0) {
      alert('Informe um preço válido.');
      return;
    }
    if (!duration || duration <= 0) {
      alert('Informe uma duração válida.');
      return;
    }

    const payload = {
      name: serviceForm.name.trim(),
      price,
      duration_minutes: duration,
      active: serviceForm.active
    };

    if (editingServiceId) {
      const { data, error } = await supabase
        .from('services')
        .update(payload)
        .eq('id', editingServiceId)
        .select();

      if (error) {
        alert(`Não foi possível atualizar o serviço: ${error.message}`);
        return;
      }
      if (data?.[0]) {
        setServices(prev => prev.map(s => s.id === editingServiceId ? data[0] : s));
      }
    } else {
      const { data, error } = await supabase
        .from('services')
        .insert(payload)
        .select();

      if (error) {
        alert(`Não foi possível cadastrar o serviço: ${error.message}`);
        return;
      }
      if (data?.[0]) setServices(prev => [data[0], ...prev]);
    }

    resetServiceForm();
    setShowServiceForm(false);
  };

  const toggleServiceActive = async (service) => {
    const active = service.active === false;
    const { data, error } = await supabase
      .from('services')
      .update({ active })
      .eq('id', service.id)
      .select();

    if (error) {
      alert(`Não foi possível alterar o serviço: ${error.message}`);
      return;
    }
    if (data?.[0]) {
      setServices(prev => prev.map(s => s.id === service.id ? data[0] : s));
    }
  };

  const deleteService = async (id) => {
    const used = appointments.some(a => a.service_id === id);
    if (used) {
      alert('Este serviço possui agendamentos no histórico. Para preservar os registros, desative o serviço em vez de excluí-lo.');
      return;
    }
    if (!window.confirm('Excluir este serviço?')) return;

    const { error } = await supabase.from('services').delete().eq('id', id);
    if (error) {
      alert(`Não foi possível excluir o serviço: ${error.message}`);
      return;
    }
    setServices(prev => prev.filter(s => s.id !== id));
  };

  const filteredServices = services.filter(service => {
    const term = serviceSearch.trim().toLowerCase();
    if (!term) return true;
    return String(service.name || '').toLowerCase().includes(term);
  });

  const activeServicesCount = services.filter(service => service.active !== false).length;
  const averageServicePrice = services.length
    ? services.reduce((sum, service) => sum + Number(service.price || 0), 0) / services.length
    : 0;

  // Agendamentos
  const resetAppointmentForm = (date = selectedDate) => {
    setAppointmentForm({
      client_id: '',
      service_id: '',
      appointment_date: date,
      start_time: '',
      notes: ''
    });
    setEditingAppointmentId(null);
  };

  const openNewAppointment = () => {
    resetAppointmentForm(selectedDate);
    setShowAppointmentForm(true);
  };

  const openMaintenanceAppointment = (client) => {
    const targetDate = client.next_maintenance_date || today;
    setPendingMaintenanceClient(client);
    setSelectedDate(targetDate);
    const d = new Date(`${targetDate}T12:00:00`);
    setCalendarMonth(new Date(d.getFullYear(), d.getMonth(), 1));
    setScreen('appointments');
  };

  const openEditAppointment = (apt) => {
    setAppointmentForm({
      client_id: String(apt.client_id || ''),
      service_id: String(apt.service_id || ''),
      appointment_date: apt.appointment_date || selectedDate,
      start_time: (apt.start_time || '').slice(0, 5),
      notes: apt.notes || ''
    });
    setEditingAppointmentId(apt.id);
    setShowAppointmentForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const addAppointment = async (event) => {
    event.preventDefault();

    if (!appointmentForm.client_id || !appointmentForm.service_id ||
        !appointmentForm.appointment_date || !appointmentForm.start_time) {
      alert('Preencha cliente, serviço, data e horário.');
      return;
    }

    const normalizedTime = appointmentForm.start_time.slice(0, 5);
    const conflict = appointments.find(a =>
      a.id !== editingAppointmentId &&
      a.appointment_date === appointmentForm.appointment_date &&
      (a.start_time || '').slice(0, 5) === normalizedTime &&
      !['cancelled', 'no_show'].includes(a.status)
    );

    if (conflict) {
      const conflictClient = clients.find(c => c.id === conflict.client_id);
      alert(`Este horário já está ocupado${conflictClient?.name ? ` por ${conflictClient.name}` : ''}. Escolha outro horário.`);
      return;
    }

    const payload = {
      client_id: Number(appointmentForm.client_id),
      service_id: Number(appointmentForm.service_id),
      appointment_date: appointmentForm.appointment_date,
      start_time: appointmentForm.start_time,
      notes: appointmentForm.notes || null
    };

    if (editingAppointmentId) {
      const { data, error } = await supabase
        .from('appointments')
        .update(payload)
        .eq('id', editingAppointmentId)
        .select();

      if (error) {
        alert(`Não foi possível atualizar o agendamento: ${error.message}`);
        return;
      }

      if (data?.[0]) {
        setAppointments(prev => prev
          .map(a => a.id === editingAppointmentId ? data[0] : a)
          .sort((a, b) => `${a.appointment_date} ${a.start_time}`.localeCompare(`${b.appointment_date} ${b.start_time}`)));
      }
    } else {
      const { data, error } = await supabase
        .from('appointments')
        .insert({ ...payload, status: 'scheduled' })
        .select();

      if (error) {
        alert(`Não foi possível salvar o agendamento: ${error.message}`);
        return;
      }

      if (data?.[0]) {
        setAppointments(prev => [...prev, data[0]]
          .sort((a, b) => `${a.appointment_date} ${a.start_time}`.localeCompare(`${b.appointment_date} ${b.start_time}`)));
      }
    }

    setSelectedDate(appointmentForm.appointment_date);
    resetAppointmentForm(appointmentForm.appointment_date);
    setShowAppointmentForm(false);
  };

  const updateAppointment = async (id, updates) => {
    const appointment = appointments.find(a => a.id === id);
    const { error } = await supabase.from('appointments').update(updates).eq('id', id);
    if (error) {
      alert(`Não foi possível atualizar: ${error.message}`);
      return;
    }

    setAppointments(prev => prev.map(a => a.id === id ? { ...a, ...updates } : a));

    // Ao concluir um atendimento, cria uma entrada no caixa apenas uma vez.
    if (updates.status === 'completed' && appointment) {
      const service = services.find(s => s.id === appointment.service_id);
      const client = clients.find(c => c.id === appointment.client_id);

      if (service) {
        const { data: existingCash } = await supabase
          .from('cash')
          .select('id')
          .eq('appointment_id', id)
          .maybeSingle();

        if (!existingCash) {
          const payload = {
            description: `${service.name}${client?.name ? ` - ${client.name}` : ''}`,
            amount: Number(service.price || 0),
            type: 'income',
            category: 'Serviço',
            payment_method: null,
            appointment_id: id,
            date: appointment.appointment_date || new Date().toISOString().split('T')[0],
            notes: 'Lançamento automático de atendimento concluído'
          };

          const { data: cashData, error: cashError } = await supabase
            .from('cash')
            .insert(payload)
            .select();

          if (cashError) {
            console.error('Erro ao lançar atendimento no caixa:', cashError);
          } else if (cashData?.[0]) {
            setCash(prev => [cashData[0], ...prev]);
          }
        }
      }

      // Se este atendimento for uma manutenção de prótese, atualiza automaticamente
      // a ficha do cliente e calcula a próxima manutenção.
      const isMaintenance = String(appointment.notes || '').toLowerCase().includes('manutenção');
      if (client && isMaintenance) {
        const interval = Number(client.maintenance_interval_days || 0);
        const completedDate = appointment.appointment_date || today;
        let nextDate = null;

        if (interval > 0) {
          const d = new Date(`${completedDate}T12:00:00`);
          d.setDate(d.getDate() + interval);
          nextDate = d.toISOString().split('T')[0];
        }

        const maintenanceUpdate = {
          last_maintenance_date: completedDate,
          next_maintenance_date: nextDate
        };

        const { error: maintenanceError } = await supabase
          .from('clients')
          .update(maintenanceUpdate)
          .eq('id', client.id);

        if (maintenanceError) {
          console.error('Erro ao atualizar manutenção do cliente:', maintenanceError);
        } else {
          setClients(prev => prev.map(c => c.id === client.id ? {...c, ...maintenanceUpdate} : c));
        }
      }
    }
  };

  useEffect(() => {
    if (screen !== 'appointments' || !pendingMaintenanceClient) return;
    const targetDate = pendingMaintenanceClient.next_maintenance_date || today;
    setAppointmentForm({client_id:String(pendingMaintenanceClient.id),service_id:'',appointment_date:targetDate,start_time:'',notes:'Manutenção de prótese capilar'});
    setEditingAppointmentId(null);
    setShowAppointmentForm(true);
    setPendingMaintenanceClient(null);
  }, [screen, pendingMaintenanceClient]);

  const deleteAppointment = async (id) => {
    if (!window.confirm('Excluir este agendamento?')) return;
    const { error } = await supabase.from('appointments').delete().eq('id', id);
    if (error) {
      alert(`Não foi possível excluir: ${error.message}`);
      return;
    }
    setAppointments(appointments.filter(a => a.id !== id));
  };

  const moveAgendaDay = (amount) => {
    const date = new Date(`${selectedDate}T12:00:00`);
    date.setDate(date.getDate() + amount);
    setSelectedDate(date.toISOString().split('T')[0]);
  };

  const agendaDateLabel = new Date(`${selectedDate}T12:00:00`).toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  });

  const dayAppointments = appointments
    .filter(a => a.appointment_date === selectedDate)
    .sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));

  const statusLabel = (status) => ({
    scheduled: 'Agendado',
    confirmed: 'Confirmado',
    completed: 'Concluído',
    cancelled: 'Cancelado',
    no_show: 'Faltou'
  }[status] || 'Agendado');

  const statusColor = (status) => ({
    scheduled: '#d4af37',
    confirmed: '#2e9d62',
    completed: '#3478c7',
    cancelled: '#c94b4b',
    no_show: '#777'
  }[status] || '#d4af37');

  // Caixa 2.0
  const resetCashForm = (type = 'income') => {
    setCashForm({
      type,
      description: '',
      amount: '',
      payment_method: 'pix',
      category: type === 'income' ? 'Serviço' : 'Despesa geral',
      date: new Date().toISOString().split('T')[0],
      notes: ''
    });
  };

  const openCashForm = (type = 'income') => {
    resetCashForm(type);
    setShowCashForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const saveCash = async (event) => {
    event.preventDefault();

    const amount = Number(String(cashForm.amount).replace(',', '.'));
    if (!cashForm.description.trim() || !amount || amount <= 0 || !cashForm.date) {
      alert('Preencha descrição, valor e data.');
      return;
    }

    const payload = {
      type: cashForm.type,
      description: cashForm.description.trim(),
      amount,
      payment_method: cashForm.payment_method || null,
      category: cashForm.category || null,
      date: cashForm.date,
      notes: cashForm.notes.trim() || null
    };

    const { data, error } = await supabase.from('cash').insert(payload).select();

    if (error) {
      alert(`Não foi possível salvar o movimento: ${error.message}`);
      return;
    }

    if (data?.[0]) setCash(prev => [data[0], ...prev]);
    setShowCashForm(false);
    resetCashForm('income');
  };

  const deleteCash = async (id) => {
    if (!window.confirm('Excluir este lançamento do caixa?')) return;
    const { error } = await supabase.from('cash').delete().eq('id', id);
    if (error) {
      alert(`Não foi possível excluir: ${error.message}`);
      return;
    }
    setCash(prev => prev.filter(c => c.id !== id));
  };

  const money = (value) =>
    Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  const paymentLabel = (method) => ({
    pix: 'Pix',
    cash: 'Dinheiro',
    debit: 'Débito',
    credit: 'Crédito'
  }[method] || 'Não informado');

  const today = new Date().toISOString().split('T')[0];
  const currentMonth = today.slice(0, 7);

  const cashIncome = cash
    .filter(item => (item.type || 'income') === 'income')
    .reduce((sum, item) => sum + Number(item.amount || 0), 0);

  const cashExpenses = cash
    .filter(item => item.type === 'expense')
    .reduce((sum, item) => sum + Number(item.amount || 0), 0);

  const totalCash = cashIncome - cashExpenses;

  const todayIncome = cash
    .filter(item => item.date === today && (item.type || 'income') === 'income')
    .reduce((sum, item) => sum + Number(item.amount || 0), 0);

  const monthIncome = cash
    .filter(item => String(item.date || '').slice(0, 7) === currentMonth && (item.type || 'income') === 'income')
    .reduce((sum, item) => sum + Number(item.amount || 0), 0);

  const monthExpenses = cash
    .filter(item => String(item.date || '').slice(0, 7) === currentMonth && item.type === 'expense')
    .reduce((sum, item) => sum + Number(item.amount || 0), 0);

  const monthBalance = monthIncome - monthExpenses;

  const filteredCash = cash
    .filter(item => {
      if (cashFilter === 'today') return item.date === today;
      if (cashFilter === 'month') return String(item.date || '').slice(0, 7) === currentMonth;
      return true;
    })
    .filter(item => {
      const term = cashSearch.trim().toLowerCase();
      if (!term) return true;
      return [item.description, item.category, item.payment_method, item.notes]
        .filter(Boolean)
        .some(value => String(value).toLowerCase().includes(term));
    })
    .sort((a, b) => `${b.date || ''} ${b.created_at || ''}`.localeCompare(`${a.date || ''} ${a.created_at || ''}`));

  const todayAppointments = appointments.filter(a => a.appointment_date === today).length;


  // Estoque 2.0
  const resetInventoryForm = () => {
    setInventoryForm({ name: '', quantity: '', min_quantity: '', cost_price: '', sale_price: '', unit: 'un' });
    setEditingInventoryId(null);
  };

  const openNewInventory = () => {
    resetInventoryForm();
    setShowInventoryForm(true);
  };

  const openEditInventory = (item) => {
    setInventoryForm({
      name: item.name || '',
      quantity: item.quantity ?? '',
      min_quantity: item.min_quantity ?? '',
      cost_price: item.cost_price ?? '',
      sale_price: item.sale_price ?? '',
      unit: item.unit || 'un'
    });
    setEditingInventoryId(item.id);
    setShowInventoryForm(true);
  };

  const saveInventory = async (event) => {
    event.preventDefault();
    const payload = {
      name: inventoryForm.name.trim(),
      quantity: Number(inventoryForm.quantity || 0),
      min_quantity: Number(inventoryForm.min_quantity || 0),
      cost_price: Number(String(inventoryForm.cost_price || 0).replace(',', '.')),
      sale_price: Number(String(inventoryForm.sale_price || 0).replace(',', '.')),
      unit: inventoryForm.unit || 'un'
    };
    if (!payload.name) return alert('Informe o nome do produto.');

    const query = editingInventoryId
      ? supabase.from('inventory').update(payload).eq('id', editingInventoryId).select()
      : supabase.from('inventory').insert(payload).select();

    const { data, error } = await query;
    if (error) return alert(`Não foi possível salvar o produto: ${error.message}`);

    if (data?.[0]) {
      setInventory(prev => editingInventoryId
        ? prev.map(i => i.id === editingInventoryId ? data[0] : i)
        : [data[0], ...prev]);
    }
    resetInventoryForm();
    setShowInventoryForm(false);
  };

  const resetInventoryMovementForm = () => {
    setInventoryMovementForm({
      inventory_id: '',
      movement_type: 'sale',
      quantity: 1,
      unit_price: '',
      payment_method: 'pix',
      notes: ''
    });
  };

  const openInventoryMovement = (item, movementType = 'sale') => {
    setInventoryMovementForm({
      inventory_id: String(item.id),
      movement_type: movementType,
      quantity: 1,
      unit_price: movementType === 'sale' ? String(item.sale_price || '') : String(item.cost_price || ''),
      payment_method: 'pix',
      notes: ''
    });
    setShowInventoryMovementForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const saveInventoryMovement = async (event) => {
    event.preventDefault();

    const item = inventory.find(i => String(i.id) === String(inventoryMovementForm.inventory_id));
    if (!item) return alert('Selecione um produto.');

    const quantity = Number(inventoryMovementForm.quantity || 0);
    const unitPrice = Number(String(inventoryMovementForm.unit_price || 0).replace(',', '.'));
    const type = inventoryMovementForm.movement_type;

    if (!quantity || quantity <= 0) return alert('Informe uma quantidade válida.');
    if (unitPrice < 0 || Number.isNaN(unitPrice)) return alert('Informe um valor válido.');

    const isEntry = type === 'restock';
    const currentQuantity = Number(item.quantity || 0);
    const newQuantity = isEntry ? currentQuantity + quantity : currentQuantity - quantity;

    if (!isEntry && newQuantity < 0) {
      return alert(`Estoque insuficiente. Disponível: ${currentQuantity} ${item.unit || 'un'}.`);
    }

    // 1. Atualiza quantidade do produto
    const { data: updatedInventory, error: inventoryError } = await supabase
      .from('inventory')
      .update({ quantity: newQuantity })
      .eq('id', item.id)
      .select();

    if (inventoryError) return alert(`Não foi possível atualizar o estoque: ${inventoryError.message}`);

    // 2. Registra histórico
    const movementPayload = {
      inventory_id: item.id,
      movement_type: type,
      quantity,
      unit_price: unitPrice,
      payment_method: type === 'sale' ? (inventoryMovementForm.payment_method || null) : null,
      notes: inventoryMovementForm.notes.trim() || null
    };

    const { data: movementData, error: movementError } = await supabase
      .from('inventory_movements')
      .insert(movementPayload)
      .select();

    if (movementError) {
      // Reverte a quantidade caso o histórico falhe
      await supabase.from('inventory').update({ quantity: currentQuantity }).eq('id', item.id);
      return alert(`Não foi possível registrar a movimentação: ${movementError.message}`);
    }

    // 3. Venda -> entrada automática no Caixa
    if (type === 'sale') {
      const cashPayload = {
        type: 'income',
        description: `Venda de produto - ${item.name}`,
        amount: quantity * unitPrice,
        payment_method: inventoryMovementForm.payment_method || null,
        category: 'Produto',
        date: new Date().toISOString().split('T')[0],
        notes: `Lançamento automático do estoque: ${quantity} ${item.unit || 'un'}`
      };

      const { data: cashData, error: cashError } = await supabase.from('cash').insert(cashPayload).select();

      if (cashError) {
        alert(`A venda foi registrada e o estoque foi baixado, mas o Caixa não recebeu o lançamento: ${cashError.message}`);
      } else if (cashData?.[0]) {
        setCash(prev => [cashData[0], ...prev]);
      }
    }

    // 4. Reposição -> despesa automática quando houver custo
    if (type === 'restock' && unitPrice > 0) {
      const cashPayload = {
        type: 'expense',
        description: `Reposição de estoque - ${item.name}`,
        amount: quantity * unitPrice,
        payment_method: inventoryMovementForm.payment_method || null,
        category: 'Estoque',
        date: new Date().toISOString().split('T')[0],
        notes: `Lançamento automático de reposição: ${quantity} ${item.unit || 'un'}`
      };

      const { data: cashData, error: cashError } = await supabase.from('cash').insert(cashPayload).select();
      if (!cashError && cashData?.[0]) setCash(prev => [cashData[0], ...prev]);
    }

    if (updatedInventory?.[0]) {
      setInventory(prev => prev.map(i => i.id === item.id ? updatedInventory[0] : i));
    }
    if (movementData?.[0]) setInventoryMovements(prev => [movementData[0], ...prev]);

    setShowInventoryMovementForm(false);
    resetInventoryMovementForm();
  };

  const inventoryMovementLabel = (type) => ({
    sale: 'Venda',
    restock: 'Reposição',
    internal_use: 'Uso interno',
    loss: 'Perda / Ajuste'
  }[type] || type);

  const deleteInventory = async (id) => {
    if (!window.confirm('Excluir este produto do estoque?')) return;
    const { error } = await supabase.from('inventory').delete().eq('id', id);
    if (error) return alert(`Não foi possível excluir: ${error.message}`);
    setInventory(prev => prev.filter(i => i.id !== id));
  };

  const filteredInventory = inventory.filter(item =>
    !inventorySearch.trim() || String(item.name || '').toLowerCase().includes(inventorySearch.trim().toLowerCase())
  );
  const lowStockCount = inventory.filter(i => Number(i.quantity || 0) <= Number(i.min_quantity || 0)).length;
  const inventoryCostValue = inventory.reduce((sum, i) => sum + Number(i.quantity || 0) * Number(i.cost_price || 0), 0);
  const inventorySaleValue = inventory.reduce((sum, i) => sum + Number(i.quantity || 0) * Number(i.sale_price || 0), 0);

  if (!authReady || (session && loading)) {
    return <div className="app-loading">Carregando...</div>;
  }

  if (recoveryMode) {
    return (
      <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:'#111',padding:'24px'}}>
        <form onSubmit={handleUpdatePassword} style={{width:'100%',maxWidth:'400px',background:'#1c1c1c',border:'1px solid #333',borderRadius:'16px',padding:'32px',color:'#fff',boxSizing:'border-box'}}>
          <img src="/bion-logo.png" alt="Bion Prótese Capilar" style={{width:'112px',height:'112px',borderRadius:'50%',objectFit:'cover',display:'block',margin:'0 auto 20px',border:'2px solid #77746b'}} />
          <div style={{color:'#b9b5aa',fontSize:'12px',fontWeight:700,letterSpacing:'3px',marginBottom:'8px',textAlign:'center'}}>BION PRÓTESE CAPILAR</div>
          <h1 style={{margin:'0 0 8px',fontSize:'28px',textAlign:'center'}}>Criar nova senha</h1>
          <p style={{margin:'0 0 24px',color:'#aaa'}}>Digite a nova senha que deseja usar para acessar o sistema.</p>

          <label style={{display:'block',marginBottom:'8px'}}>Nova senha</label>
          <input type="password" value={newPassword} onChange={(e)=>setNewPassword(e.target.value)} required minLength={6} autoComplete="new-password"
            style={{width:'100%',boxSizing:'border-box',padding:'12px',marginBottom:'16px',borderRadius:'8px',border:'1px solid #444',background:'#111',color:'#fff'}} />

          <label style={{display:'block',marginBottom:'8px'}}>Confirmar nova senha</label>
          <input type="password" value={confirmNewPassword} onChange={(e)=>setConfirmNewPassword(e.target.value)} required minLength={6} autoComplete="new-password"
            style={{width:'100%',boxSizing:'border-box',padding:'12px',marginBottom:'12px',borderRadius:'8px',border:'1px solid #444',background:'#111',color:'#fff'}} />

          {passwordMessage && <div style={{marginBottom:'14px',color:passwordMessage.startsWith('Senha alterada') ? '#8fe3a8' : '#ff8a8a'}}>{passwordMessage}</div>}

          <button type="submit" disabled={passwordLoading}
            style={{width:'100%',padding:'12px',border:0,borderRadius:'8px',background:'#d4af37',color:'#111',fontWeight:800,cursor:'pointer'}}>
            {passwordLoading ? 'Salvando...' : 'Salvar nova senha'}
          </button>
        </form>
      </div>
    );
  }

  if (!session) {
    return (
      <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:'#111',padding:'24px'}}>
        <form onSubmit={handleLogin} style={{width:'100%',maxWidth:'400px',background:'#1c1c1c',border:'1px solid #333',borderRadius:'16px',padding:'32px',color:'#fff',boxSizing:'border-box'}}>
          <img src="/bion-logo.png" alt="Bion Prótese Capilar" style={{width:'112px',height:'112px',borderRadius:'50%',objectFit:'cover',display:'block',margin:'0 auto 20px',border:'2px solid #77746b'}} />
          <div style={{color:'#b9b5aa',fontSize:'12px',fontWeight:700,letterSpacing:'3px',marginBottom:'8px',textAlign:'center'}}>BION PRÓTESE CAPILAR</div>
          <h1 style={{margin:'0 0 8px',fontSize:'28px',textAlign:'center'}}>Acesso ao sistema</h1>
          <p style={{margin:'0 0 24px',color:'#aaa'}}>Entre com seu e-mail e senha.</p>
          <label style={{display:'block',marginBottom:'8px'}}>E-mail</label>
          <input type="email" value={email} onChange={(e)=>setEmail(e.target.value)} required autoComplete="email"
            style={{width:'100%',boxSizing:'border-box',padding:'12px',marginBottom:'16px',borderRadius:'8px',border:'1px solid #444',background:'#111',color:'#fff'}} />
          <label style={{display:'block',marginBottom:'8px'}}>Senha</label>
          <input type="password" value={password} onChange={(e)=>setPassword(e.target.value)} required autoComplete="current-password"
            style={{width:'100%',boxSizing:'border-box',padding:'12px',marginBottom:'12px',borderRadius:'8px',border:'1px solid #444',background:'#111',color:'#fff'}} />
          {loginError && <div style={{marginBottom:'14px',color:'#ff8a8a'}}>{loginError}</div>}
          <button type="submit" disabled={loginLoading}
            style={{width:'100%',padding:'12px',border:0,borderRadius:'8px',background:'#d4af37',color:'#111',fontWeight:800,cursor:'pointer'}}>
            {loginLoading ? 'Entrando...' : 'Entrar'}
          </button>

          <button type="button" onClick={handleForgotPassword} disabled={passwordLoading}
            style={{width:'100%',marginTop:'14px',padding:'8px',border:0,background:'transparent',color:'#d4af37',fontWeight:700,cursor:'pointer'}}>
            {passwordLoading ? 'Enviando...' : 'Esqueci minha senha'}
          </button>

          {recoveryEmailSent && (
            <div style={{marginTop:'10px',padding:'11px',borderRadius:'8px',background:'#173321',color:'#9be6b1',fontSize:'13px',lineHeight:1.45}}>
              Link de recuperação enviado. Abra o e-mail e clique no link para criar uma nova senha.
            </div>
          )}
        </form>
      </div>
    );
  }

  return (
    <div className="app">
      <style>{`
        :root{--bion-dark:#181818;--bion-dark2:#232323;--bion-taupe:#817e73;--bion-accent:#a99a72;--bion-bg:#f4f3f0;--bion-line:#dedbd4;}
        body{background:var(--bion-bg)!important;}
        .app{min-height:100vh;background:var(--bion-bg)!important;}
        .app-header{
          position:fixed!important;left:0!important;top:0!important;width:250px!important;height:132px!important;
          box-sizing:border-box!important;background:linear-gradient(145deg,#151515,#242424)!important;
          padding:22px 20px!important;z-index:100!important;border:0!important;border-right:1px solid #343434!important;
          display:flex!important;align-items:flex-start!important;justify-content:flex-start!important;gap:0!important;
          box-shadow:none!important;
        }
        .bion-lockup{display:flex;align-items:center;gap:12px;width:100%;}
        .bion-logo{width:62px;height:62px;border-radius:50%;object-fit:cover;border:1px solid #77746b;flex:none;}
        .bion-name{display:flex;flex-direction:column;line-height:1;}
        .bion-name strong{font-size:21px;letter-spacing:4px;color:#fff;font-weight:500;}
        .bion-name span{font-size:7px;letter-spacing:2.2px;color:#aaa69b;margin-top:8px;white-space:nowrap;}
        .mobile-screen-name{display:none;}
        .bion-logout{position:absolute;left:20px;bottom:13px;border:0!important;background:transparent!important;color:#999!important;padding:0!important;font-size:12px;cursor:pointer;}
        .bion-logout:hover{color:#fff!important;}
        .app-nav{
          position:fixed!important;left:0!important;top:132px!important;bottom:0!important;width:250px!important;height:auto!important;
          display:flex!important;flex-direction:column!important;justify-content:flex-start!important;gap:6px!important;
          padding:16px 12px!important;box-sizing:border-box!important;background:#1d1d1d!important;border:0!important;
          border-right:1px solid #343434!important;z-index:99!important;transform:none!important;margin:0!important;
        }
        .nav-btn{
          width:100%!important;min-height:48px!important;display:flex!important;flex-direction:row!important;align-items:center!important;
          justify-content:flex-start!important;gap:13px!important;padding:0 15px!important;border:0!important;border-radius:10px!important;
          background:transparent!important;color:#aaa!important;font-size:17px!important;cursor:pointer!important;
        }
        .nav-btn span{width:22px;text-align:center;font-size:18px;}
        .nav-btn b{font-size:13px;font-weight:600;letter-spacing:.2px;}
        .nav-btn:hover{background:#292929!important;color:#fff!important;}
        .nav-btn.active{background:#f1efe9!important;color:#282722!important;box-shadow:inset 3px 0 0 #9a8f70!important;}
        .screen-content{
          width:auto!important;max-width:1120px!important;margin-left:250px!important;margin-right:auto!important;
          padding:38px 42px 110px!important;box-sizing:border-box!important;
        }
        .item-card{border-color:var(--bion-line)!important;border-radius:13px!important;box-shadow:0 2px 8px rgba(0,0,0,.035)!important;}
        .btn-primary{background:#9b8d67!important;color:#fff!important;border:0!important;}
        .btn-primary:hover{filter:brightness(.94);}
        @media(min-width:701px){
          .screen-content{max-width:1370px!important;}
        }

        /* Bion 3.1 — melhor aproveitamento do desktop */
        @media(min-width:701px){
          .screen-content{
            margin-left:250px!important;
            width:calc(100% - 250px)!important;
            max-width:none!important;
            padding:34px 42px 110px!important;
          }
          .dashboard-shell,
          .dashboard-container,
          .dashboard-content{
            width:100%!important;
            max-width:none!important;
          }
          .dashboard-stats,
          .stats-grid{
            width:100%!important;
            max-width:none!important;
            display:grid!important;
            grid-template-columns:repeat(3,minmax(190px,1fr))!important;
            gap:14px!important;
          }
          .dashboard-actions,
          .quick-actions{
            width:100%!important;
            max-width:none!important;
            display:grid!important;
            grid-template-columns:repeat(3,minmax(180px,1fr))!important;
            gap:12px!important;
          }
          .dashboard-actions button,
          .quick-actions button{
            width:100%!important;
            min-height:48px!important;
          }
          .dashboard-lower,
          .dashboard-bottom{
            width:100%!important;
            max-width:none!important;
            display:grid!important;
            grid-template-columns:minmax(300px,.8fr) minmax(430px,1.2fr)!important;
            gap:16px!important;
            align-items:start!important;
          }
          .nav-btn{color:#c5c2bb!important;}
          .nav-btn b{font-size:14px!important;}
          .nav-btn span{color:#a8a49a!important;}
          .nav-btn.active{color:#24231f!important;}
          .nav-btn.active span{color:#8d805f!important;}
        }


        /* Bion 3.2 — refinamento final do desktop */
        @media(min-width:701px){
          .screen-content{
            width:calc(100% - 250px)!important;
            max-width:1540px!important;
            margin-left:250px!important;
            margin-right:auto!important;
            padding:34px 36px 110px!important;
          }
          @media(min-width:1790px){
            .screen-content{
              margin-left:calc(250px + (100vw - 1790px)/2)!important;
            }
          }
          .nav-btn{
            color:#d0cdc5!important;
            transition:background .18s ease,color .18s ease,transform .18s ease!important;
          }
          .nav-btn:hover{transform:translateX(2px);}
          .nav-btn b{font-size:14px!important;font-weight:650!important;}
          .nav-btn span{font-size:18px!important;color:#aaa596!important;}
          .nav-btn.active span{color:#8d7e59!important;}
          .item-card,
          .stat-card{
            background:#fff!important;
            border:1px solid #dedbd4!important;
            box-shadow:0 4px 14px rgba(28,28,28,.045)!important;
          }
          button{
            transition:transform .15s ease,filter .15s ease,box-shadow .15s ease!important;
          }
          button:hover{filter:brightness(.97);}
          .btn-primary{
            background:linear-gradient(135deg,#a49772,#8e805d)!important;
            box-shadow:0 4px 10px rgba(92,78,42,.10)!important;
          }
          .btn-primary:hover{
            transform:translateY(-1px);
            box-shadow:0 6px 14px rgba(92,78,42,.16)!important;
          }
        }

        @media(max-width:700px){
          .app-header{
            width:100%!important;height:72px!important;padding:9px 12px!important;border-right:0!important;border-bottom:1px solid #343434!important;
            align-items:center!important;
          }
          .bion-logo{width:46px;height:46px;}
          .bion-name strong{font-size:15px;letter-spacing:3px;}
          .bion-name span{font-size:6px;letter-spacing:1.7px;margin-top:6px;}
          .bion-lockup{width:auto;}
          .mobile-screen-name{display:block;margin-left:auto;margin-right:46px;color:#d6d2c7;font-size:12px;font-weight:700;}
          .bion-logout{left:auto;right:12px;bottom:auto;top:29px;font-size:10px;}
          .screen-content{width:100%!important;margin:0!important;padding:92px 12px 108px!important;max-width:none!important;}
          .app-nav{
            left:6px!important;right:6px!important;top:auto!important;bottom:6px!important;width:auto!important;height:82px!important;
            flex-direction:row!important;justify-content:space-around!important;padding:6px!important;border:1px solid #333!important;
            border-radius:18px!important;background:rgba(28,28,28,.98)!important;box-shadow:0 10px 30px rgba(0,0,0,.25)!important;
          }
          .nav-btn{width:auto!important;flex:1!important;min-width:0!important;min-height:68px!important;flex-direction:column!important;
            justify-content:center!important;gap:5px!important;padding:4px 2px!important;border-radius:12px!important;}
          .nav-btn span{
            width:auto!important;
            font-size:29px!important;
            line-height:1!important;
            color:#f1eee7!important;
            opacity:1!important;
          }
          .nav-btn b{
            font-size:10px!important;
            line-height:1.1!important;
            font-weight:800!important;
            color:#f1eee7!important;
            opacity:1!important;
          }
          .nav-btn.active{box-shadow:none!important;}
          .nav-btn.active span,
          .nav-btn.active b{
            color:#282722!important;
          }
        }
      `}</style>
      {/* Header / identidade Bion */}
      <div className="app-header">
        <div className="bion-lockup">
          <img src="/bion-logo.png" alt="Bion Prótese Capilar" className="bion-logo" />
          <div className="bion-name">
            <strong>BION</strong>
            <span>PRÓTESE CAPILAR</span>
          </div>
        </div>
        <div className="mobile-screen-name">
          {screen === 'dashboard' && 'Dashboard'}
          {screen === 'appointments' && 'Agenda'}
          {screen === 'clients' && 'Clientes'}
          {screen === 'services' && 'Serviços'}
          {screen === 'cash' && 'Caixa'}
          {screen === 'inventory' && 'Estoque'}
          {screen === 'reports' && 'Relatórios'}
        </div>
        <button className="bion-logout" onClick={handleLogout}>Sair</button>
      </div>

      {/* Dashboard 2.0 */}
      {screen === 'dashboard' && (() => {
        const todayList = appointments
          .filter(a => a.appointment_date === today && !['cancelled', 'no_show'].includes(a.status))
          .sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));

        const nowTime = new Date().toTimeString().slice(0, 5);
        const nextAppointment = todayList.find(a => (a.start_time || '').slice(0, 5) >= nowTime && a.status !== 'completed');
        const completedToday = todayList.filter(a => a.status === 'completed').length;

        const maintenanceClients = clients
          .filter(c => c.next_maintenance_date)
          .map(c => {
            const target = new Date(`${c.next_maintenance_date}T12:00:00`);
            const base = new Date(`${today}T12:00:00`);
            return {...c, maintenanceDays: Math.round((target - base) / 86400000)};
          })
          .sort((a,b) => String(a.next_maintenance_date).localeCompare(String(b.next_maintenance_date)));

        const maintenanceStatus = (days) => {
          if (days < 0) return {label:`Atrasada ${Math.abs(days)}d`, color:'#b83232', bg:'#fff0f0'};
          if (days === 0) return {label:'Hoje', color:'#9a6510', bg:'#fff7df'};
          if (days <= 7) return {label:`Em ${days}d`, color:'#9a6510', bg:'#fff7df'};
          if (days <= 30) return {label:`Em ${days}d`, color:'#62583f', bg:'#f1ede2'};
          return {label:`Em ${days}d`, color:'#66706a', bg:'#f2f4f2'};
        };

        return (
          <div className="screen-content">
            <div style={{marginBottom:'18px'}}>
              <div style={{fontSize:'13px',fontWeight:800,color:'#8b6f10',textTransform:'uppercase',letterSpacing:'1px'}}>Visão geral</div>
              <h2 style={{margin:'4px 0 5px',fontSize:'26px'}}>Resumo da Bion</h2>
              <div style={{color:'#777',fontSize:'14px'}}>Agenda, clientes, financeiro e estoque em um só lugar.</div>
            </div>

            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(155px,1fr))',gap:'12px',marginBottom:'18px'}}>
              <div className="item-card" style={{display:'block',borderLeft:'5px solid #d4af37'}}>
                <div className="stat-label">Faturamento hoje</div>
                <div style={{fontSize:'24px',fontWeight:900,marginTop:'7px'}}>{money(todayIncome)}</div>
              </div>
              <div className="item-card" style={{display:'block'}}>
                <div className="stat-label">Faturamento no mês</div>
                <div style={{fontSize:'24px',fontWeight:900,marginTop:'7px'}}>{money(monthIncome)}</div>
              </div>
              <div className="item-card" style={{display:'block'}}>
                <div className="stat-label">Despesas no mês</div>
                <div style={{fontSize:'24px',fontWeight:900,marginTop:'7px',color:'#b83232'}}>{money(monthExpenses)}</div>
              </div>
              <div className="item-card" style={{display:'block',borderLeft:'5px solid #2e9d62'}}>
                <div className="stat-label">Saldo do mês</div>
                <div style={{fontSize:'24px',fontWeight:900,marginTop:'7px'}}>{money(monthBalance)}</div>
              </div>
              <div className="item-card" style={{display:'block'}}>
                <div className="stat-label">Atendimentos hoje</div>
                <div style={{fontSize:'24px',fontWeight:900,marginTop:'7px'}}>{todayList.length}</div>
                <div style={{fontSize:'12px',color:'#777',marginTop:'4px'}}>{completedToday} concluído{completedToday === 1 ? '' : 's'}</div>
              </div>
              <div className="item-card" style={{display:'block'}}>
                <div className="stat-label">Clientes cadastrados</div>
                <div style={{fontSize:'24px',fontWeight:900,marginTop:'7px'}}>{clients.length}</div>
              </div>
            </div>

            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(260px,1fr))',gap:'12px',marginBottom:'18px'}}>
              <button className="btn-primary" onClick={() => { setSelectedDate(today); resetAppointmentForm(today); setShowAppointmentForm(true); setScreen('appointments'); }}>📅 + Novo Agendamento</button>
              <button className="btn-primary" onClick={() => { openNewClient(); setScreen('clients'); }}>👤 + Novo Cliente</button>
              <button className="btn-primary" onClick={() => { openCashForm('income'); setScreen('cash'); }}>💰 + Nova Entrada</button>
            </div>

            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))',gap:'14px'}}>
              <div className="item-card" style={{display:'block',padding:'18px'}}>
                <div style={{fontSize:'12px',fontWeight:800,color:'#8b6f10',textTransform:'uppercase',letterSpacing:'1px'}}>Próximo atendimento</div>
                {nextAppointment ? (() => {
                  const client = clients.find(c => c.id === nextAppointment.client_id);
                  const service = services.find(s => s.id === nextAppointment.service_id);
                  return (
                    <div style={{marginTop:'12px'}}>
                      <div style={{fontSize:'30px',fontWeight:900}}>{(nextAppointment.start_time || '').slice(0,5)}</div>
                      <div style={{fontSize:'19px',fontWeight:800,marginTop:'4px'}}>{client?.name || 'Cliente'}</div>
                      <div style={{color:'#666',marginTop:'4px'}}>{service?.name || 'Serviço'} • {money(service?.price || 0)}</div>
                      <div style={{marginTop:'12px'}}>
                        <span style={{padding:'6px 9px',borderRadius:'999px',background:statusColor(nextAppointment.status),color:'#fff',fontSize:'11px',fontWeight:800}}>{statusLabel(nextAppointment.status)}</span>
                      </div>
                    </div>
                  );
                })() : (
                  <div style={{padding:'24px 0 8px',textAlign:'center'}}>
                    <div style={{fontSize:'30px'}}>✂️</div>
                    <div style={{fontWeight:800,marginTop:'6px'}}>Nenhum próximo atendimento hoje</div>
                  </div>
                )}
              </div>

              <div className="item-card" style={{display:'block',padding:'18px'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:'10px',marginBottom:'12px'}}>
                  <div>
                    <div style={{fontSize:'12px',fontWeight:800,color:'#8b6f10',textTransform:'uppercase',letterSpacing:'1px'}}>Agenda de hoje</div>
                    <div style={{fontWeight:900,fontSize:'18px',marginTop:'3px'}}>{todayList.length} atendimento{todayList.length === 1 ? '' : 's'}</div>
                  </div>
                  <button type="button" onClick={() => { setSelectedDate(today); setScreen('appointments'); }} style={{border:'1px solid #bbb',background:'#fff',borderRadius:'8px',padding:'8px 10px',fontWeight:700,cursor:'pointer'}}>Ver agenda</button>
                </div>

                {todayList.length === 0 ? (
                  <div style={{color:'#777',padding:'20px 0',textAlign:'center'}}>Nenhum atendimento agendado para hoje.</div>
                ) : (
                  <div style={{display:'grid',gap:'8px'}}>
                    {todayList.slice(0,5).map(apt => {
                      const client = clients.find(c => c.id === apt.client_id);
                      const service = services.find(s => s.id === apt.service_id);
                      return (
                        <div key={apt.id} style={{border:'1px solid #e4e4e4',borderLeft:`4px solid ${statusColor(apt.status)}`,borderRadius:'9px',padding:'10px 12px',display:'flex',justifyContent:'space-between',gap:'12px',alignItems:'center'}}>
                          <div>
                            <div style={{fontWeight:900}}>{(apt.start_time || '').slice(0,5)} • {client?.name || 'Cliente'}</div>
                            <div style={{fontSize:'13px',color:'#666',marginTop:'3px'}}>{service?.name || 'Serviço'}</div>
                          </div>
                          <span style={{fontSize:'11px',fontWeight:800,color:statusColor(apt.status)}}>{statusLabel(apt.status)}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="item-card" style={{display:'block',padding:'18px',marginTop:'14px'}}>
              {(() => {
                const overdue = maintenanceClients.filter(c => c.maintenanceDays < 0);
                const upcoming = maintenanceClients.filter(c => c.maintenanceDays >= 0);
                const urgent = [...overdue, ...upcoming].slice(0,3);
                return (
                  <>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:'12px',flexWrap:'wrap',marginBottom:'13px'}}>
                      <div>
                        <div style={{fontSize:'12px',fontWeight:800,color:'#8b6f10',textTransform:'uppercase',letterSpacing:'1px'}}>Manutenções</div>
                        <div style={{fontSize:'18px',fontWeight:900,marginTop:'3px'}}>
                          {upcoming.length} próxima{upcoming.length === 1 ? '' : 's'} · {overdue.length} atrasada{overdue.length === 1 ? '' : 's'}
                        </div>
                      </div>
                      <button type="button" onClick={() => setScreen('appointments')}
                        style={{border:'1px solid #cfc8b9',background:'#fff',borderRadius:'9px',padding:'9px 13px',fontWeight:800,cursor:'pointer'}}>
                        Ver na agenda
                      </button>
                    </div>

                    {urgent.length === 0 ? (
                      <div style={{color:'#777',padding:'8px 0'}}>Nenhuma manutenção prevista.</div>
                    ) : (
                      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(240px,1fr))',gap:'9px'}}>
                        {urgent.map(client => {
                          const st = maintenanceStatus(client.maintenanceDays);
                          return (
                            <div key={client.id} style={{border:'1px solid #e4e0d8',borderRadius:'10px',padding:'11px 12px',background:'#fff'}}>
                              <div style={{display:'flex',justifyContent:'space-between',gap:'8px',alignItems:'center'}}>
                                <strong style={{fontSize:'14px'}}>{client.name}</strong>
                                <span style={{padding:'4px 7px',borderRadius:'999px',background:st.bg,color:st.color,fontSize:'10px',fontWeight:900}}>{st.label}</span>
                              </div>
                              <div style={{fontSize:'12px',color:'#6f6b63',marginTop:'5px'}}>
                                {new Date(`${client.next_maintenance_date}T12:00:00`).toLocaleDateString('pt-BR')}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        );
      })()}

      {/* Appointments */}
      {screen === 'appointments' && (
        <div className="screen-content">
          <div style={{display:'grid',gap:'14px'}}>
            <div style={{background:'#fff',border:'1px solid #ddd',borderRadius:'14px',padding:'14px',display:'flex',gap:'10px',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap'}}>
              <button className="btn-primary" style={{width:'auto',minWidth:'52px'}} onClick={() => moveAgendaDay(-1)}>‹</button>
              <div style={{textAlign:'center',flex:'1',minWidth:'220px'}}>
                <div style={{fontSize:'12px',fontWeight:800,color:'#8b6f10',textTransform:'uppercase',letterSpacing:'1px'}}>
                  {selectedDate === today ? 'Hoje' : 'Agenda do dia'}
                </div>
                <button type="button" onClick={() => {
                    const d=new Date(`${selectedDate}T12:00:00`);
                    setCalendarMonth(new Date(d.getFullYear(),d.getMonth(),1));
                    setShowAgendaCalendar(v=>!v);
                  }}
                  style={{border:0,background:'transparent',cursor:'pointer',padding:'3px 8px',display:'inline-flex',alignItems:'center',gap:'8px'}}>
                  <span style={{fontSize:'20px',fontWeight:800,textTransform:'capitalize'}}>{agendaDateLabel}</span><span>📅</span>
                </button>
                <div style={{fontSize:'11px',fontWeight:700,color:'#9a8c68',marginTop:'4px'}}>Clique na data para escolher outro dia</div>
                <div style={{fontSize:'13px',color:'#777',marginTop:'3px'}}>
                  {dayAppointments.length} {dayAppointments.length === 1 ? 'atendimento' : 'atendimentos'}
                </div>
              </div>
              <button className="btn-primary" style={{width:'auto',minWidth:'52px'}} onClick={() => moveAgendaDay(1)}>›</button>
            </div>

            {showAgendaCalendar && (() => {
              const y=calendarMonth.getFullYear(), m=calendarMonth.getMonth(), first=new Date(y,m,1).getDay(), total=new Date(y,m+1,0).getDate();
              const cells=[...Array(first).fill(null),...Array.from({length:total},(_,i)=>i+1)];
              while(cells.length%7) cells.push(null);
              return <div style={{background:'#fff',border:'1px solid #ddd',borderRadius:'14px',padding:'14px',boxShadow:'0 10px 25px rgba(0,0,0,.12)'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'10px'}}>
                  <button type="button" onClick={()=>setCalendarMonth(new Date(y,m-1,1))}>‹</button>
                  <strong style={{textTransform:'capitalize'}}>{new Intl.DateTimeFormat('pt-BR',{month:'long',year:'numeric'}).format(calendarMonth)}</strong>
                  <button type="button" onClick={()=>setCalendarMonth(new Date(y,m+1,1))}>›</button>
                </div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:'5px',textAlign:'center'}}>
                  {['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'].map(x=><b key={x} style={{fontSize:'11px',color:'#777'}}>{x}</b>)}
                  {cells.map((d,i)=>!d?<div key={i}/>:(()=>{
                    const date=`${y}-${String(m+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`, sel=date===selectedDate;
                    const hasMaintenance=clients.some(c=>c.next_maintenance_date===date);
                    const hasAppointment=appointments.some(a=>a.appointment_date===date&&!['cancelled','no_show'].includes(a.status));
                    return <button key={date} type="button" onClick={()=>{setSelectedDate(date);setShowAgendaCalendar(false);}}
                      style={{minHeight:'42px',position:'relative',borderRadius:'8px',border:sel?'2px solid #a79769':'1px solid #eee',background:sel?'#a79769':'#fff',color:sel?'#fff':'#111',fontWeight:800,cursor:'pointer'}}>
                      {d}
                      {(hasMaintenance||hasAppointment)&&<span style={{position:'absolute',bottom:'3px',left:'50%',transform:'translateX(-50%)',display:'flex',gap:'3px'}}>
                        {hasAppointment&&<i style={{display:'block',width:'5px',height:'5px',borderRadius:'50%',background:sel?'#fff':'#777'}} />}
                        {hasMaintenance&&<i style={{display:'block',width:'5px',height:'5px',borderRadius:'50%',background:sel?'#fff':'#a79769'}} />}
                      </span>}
                    </button>
                  })())}
                </div>
              </div>;
            })()}

            <div style={{display:'flex',gap:'10px',flexWrap:'wrap'}}>
              <button className="btn-primary" style={{flex:'1',minWidth:'180px'}} onClick={() => setSelectedDate(today)}>Hoje</button>
              <button className="btn-primary" style={{flex:'2',minWidth:'220px'}} onClick={openNewAppointment}>+ Novo Agendamento</button>
            </div>
          </div>

          {showAppointmentForm && (
            <form onSubmit={addAppointment} className="item-card" style={{display:'grid',gap:'12px',marginTop:'16px',alignItems:'stretch'}}>
              <div style={{fontSize:'18px',fontWeight:800}}>
                {editingAppointmentId ? 'Editar agendamento' : 'Novo agendamento'}
              </div>

              <label style={{fontSize:'13px',fontWeight:700}}>Cliente</label>
              <select className="item-input" required value={appointmentForm.client_id}
                onChange={(e) => setAppointmentForm({...appointmentForm, client_id:e.target.value})}>
                <option value="">Selecione o cliente</option>
                {clients.map(client => <option key={client.id} value={client.id}>{client.name}</option>)}
              </select>

              <label style={{fontSize:'13px',fontWeight:700}}>Serviço</label>
              <select className="item-input" required value={appointmentForm.service_id}
                onChange={(e) => setAppointmentForm({...appointmentForm, service_id:e.target.value})}>
                <option value="">Selecione o serviço</option>
                {services.filter(service => service.active !== false).map(service => (
                  <option key={service.id} value={service.id}>
                    {service.name} — R$ {Number(service.price || 0).toFixed(2)}
                  </option>
                ))}
              </select>

              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))',gap:'12px'}}>
                <div>
                  <label style={{fontSize:'13px',fontWeight:700,display:'block',marginBottom:'6px'}}>Data</label>
                  <input className="item-input" style={{width:'100%',boxSizing:'border-box'}} type="date" required value={appointmentForm.appointment_date}
                    onChange={(e) => setAppointmentForm({...appointmentForm, appointment_date:e.target.value})} />
                </div>
                <div>
                  <label style={{fontSize:'13px',fontWeight:700,display:'block',marginBottom:'6px'}}>Horário</label>
                  <input className="item-input" style={{width:'100%',boxSizing:'border-box'}} type="time" required value={appointmentForm.start_time}
                    onChange={(e) => setAppointmentForm({...appointmentForm, start_time:e.target.value})} />
                </div>
              </div>

              <label style={{fontSize:'13px',fontWeight:700}}>Observações</label>
              <input className="item-input" type="text" placeholder="Ex.: cliente prefere máquina 1 nas laterais"
                value={appointmentForm.notes}
                onChange={(e) => setAppointmentForm({...appointmentForm, notes:e.target.value})} />

              <div style={{display:'flex',gap:'10px',flexWrap:'wrap'}}>
                <button className="btn-primary" style={{flex:'1'}} type="submit">
                  {editingAppointmentId ? 'Salvar alterações' : 'Salvar Agendamento'}
                </button>
                <button type="button"
                  onClick={() => { setShowAppointmentForm(false); resetAppointmentForm(selectedDate); }}
                  style={{padding:'12px 18px',borderRadius:'9px',border:'1px solid #ccc',background:'#fff',fontWeight:700,cursor:'pointer'}}>
                  Cancelar
                </button>
              </div>
            </form>
          )}

          {(() => {
            const maintenanceForDay = clients
              .filter(c => c.next_maintenance_date === selectedDate)
              .sort((a,b) => String(a.name || '').localeCompare(String(b.name || '')));

            if (maintenanceForDay.length === 0) return null;

            return (
              <div className="item-card" style={{display:'block',padding:'16px',marginTop:'18px',border:'1px solid #d8cfb8',background:'#fbfaf6'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:'10px',flexWrap:'wrap',marginBottom:'12px'}}>
                  <div>
                    <div style={{fontSize:'11px',fontWeight:900,color:'#8b6f10',textTransform:'uppercase',letterSpacing:'1px'}}>Manutenções previstas</div>
                    <div style={{fontSize:'17px',fontWeight:900,marginTop:'3px'}}>
                      {maintenanceForDay.length} para este dia
                    </div>
                  </div>
                </div>

                <div style={{display:'grid',gap:'9px'}}>
                  {maintenanceForDay.map(client => {
                    const alreadyScheduled = appointments.some(a =>
                      a.client_id === client.id &&
                      a.appointment_date === selectedDate &&
                      String(a.notes || '').toLowerCase().includes('manutenção') &&
                      a.status !== 'cancelled'
                    );

                    return (
                      <div key={client.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:'12px',padding:'11px 12px',background:'#fff',border:'1px solid #e5e0d5',borderRadius:'10px',flexWrap:'wrap'}}>
                        <div>
                          <div style={{fontWeight:900}}>{client.name}</div>
                          <div style={{fontSize:'12px',color:'#6f6b63',marginTop:'3px'}}>
                            {client.hair_system_model || 'Prótese capilar'}
                            {client.maintenance_interval_days ? ` · intervalo ${client.maintenance_interval_days} dias` : ''}
                          </div>
                        </div>

                        {alreadyScheduled ? (
                          <span style={{padding:'7px 10px',borderRadius:'999px',background:'#e9f4ec',color:'#39714a',fontSize:'11px',fontWeight:900}}>
                            Agendada
                          </span>
                        ) : (
                          <button type="button" className="btn-primary" onClick={() => openMaintenanceAppointment(client)}
                            style={{width:'auto',padding:'9px 13px',whiteSpace:'nowrap'}}>
                            Agendar
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          <div style={{marginTop:'18px',display:'grid',gap:'12px'}}>
            {dayAppointments.length === 0 && (
              <div className="item-card" style={{display:'block',textAlign:'center',padding:'30px 20px'}}>
                <div style={{fontSize:'34px',marginBottom:'8px'}}>✂️</div>
                <div style={{fontWeight:800}}>Nenhum atendimento neste dia</div>
                <div style={{color:'#777',fontSize:'14px',marginTop:'5px'}}>Use “Novo Agendamento” para adicionar um horário.</div>
              </div>
            )}

            {dayAppointments.map(apt => {
              const client = clients.find(c => c.id === apt.client_id);
              const service = services.find(s => s.id === apt.service_id);
              const timeText = (apt.start_time || '').slice(0, 5);
              const price = Number(service?.price || 0);

              return (
                <div key={apt.id} className="item-card" style={{borderLeft:`5px solid ${statusColor(apt.status)}`,alignItems:'stretch',gap:'14px'}}>
                  <div style={{display:'flex',gap:'14px',flex:'1',alignItems:'flex-start'}}>
                    <div style={{minWidth:'62px',fontSize:'22px',fontWeight:900,color:'#222'}}>{timeText}</div>
                    <div style={{flex:'1'}}>
                      <div className="item-title" style={{fontSize:'18px'}}>{client?.name || 'Cliente'}</div>
                      <div className="item-subtitle" style={{marginTop:'4px'}}>
                        {service?.name || 'Serviço'} • R$ {price.toFixed(2)}
                      </div>
                      {service?.duration_minutes && (
                        <div className="item-subtitle">Duração: {service.duration_minutes} min</div>
                      )}
                      {apt.notes && <div className="item-subtitle" style={{marginTop:'6px'}}>📝 {apt.notes}</div>}
                    </div>
                  </div>

                  <div className="item-actions" style={{display:'flex',gap:'8px',alignItems:'center',flexWrap:'wrap',justifyContent:'flex-end'}}>
                    <span style={{padding:'7px 10px',borderRadius:'999px',background:statusColor(apt.status),color:'#fff',fontSize:'12px',fontWeight:800}}>
                      {statusLabel(apt.status)}
                    </span>

                    <select value={apt.status}
                      onChange={(e) => updateAppointment(apt.id, {status:e.target.value})}
                      className="item-select">
                      <option value="scheduled">Agendado</option>
                      <option value="confirmed">Confirmado</option>
                      <option value="completed">Concluído</option>
                      <option value="cancelled">Cancelado</option>
                      <option value="no_show">Faltou</option>
                    </select>

                    <button type="button" onClick={() => openEditAppointment(apt)}
                      style={{border:'1px solid #bbb',background:'#fff',borderRadius:'8px',padding:'9px 12px',fontWeight:700,cursor:'pointer'}}>
                      Editar
                    </button>
                    <button className="btn-delete" onClick={() => deleteAppointment(apt.id)}>✕</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Clients */}
      {screen === 'clients' && (
        <div className="screen-content">
          <div style={{display:'grid',gap:'12px'}}>
            <div style={{display:'flex',gap:'10px',flexWrap:'wrap'}}>
              <input
                className="item-input"
                style={{flex:'2',minWidth:'220px',boxSizing:'border-box'}}
                type="search"
                placeholder="🔎 Buscar por nome, telefone ou e-mail..."
                value={clientSearch}
                onChange={(e) => setClientSearch(e.target.value)}
              />
              <button className="btn-primary" style={{flex:'1',minWidth:'190px'}} onClick={openNewClient}>
                + Novo Cliente
              </button>
            </div>

            <div style={{fontSize:'13px',color:'#777'}}>
              {filteredClients.length} {filteredClients.length === 1 ? 'cliente encontrado' : 'clientes encontrados'}
            </div>
          </div>

          {showClientForm && (
            <form onSubmit={saveClient} className="item-card" style={{display:'grid',gap:'12px',marginTop:'16px',alignItems:'stretch'}}>
              <div style={{fontSize:'19px',fontWeight:800}}>
                {editingClientId ? 'Editar cliente' : 'Novo cliente'}
              </div>

              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))',gap:'12px'}}>
                <div>
                  <label style={{fontSize:'13px',fontWeight:700,display:'block',marginBottom:'6px'}}>Nome *</label>
                  <input className="item-input" style={{width:'100%',boxSizing:'border-box'}} required
                    placeholder="Nome completo"
                    value={clientForm.name}
                    onChange={(e) => setClientForm({...clientForm, name:e.target.value})} />
                </div>
                <div>
                  <label style={{fontSize:'13px',fontWeight:700,display:'block',marginBottom:'6px'}}>Telefone</label>
                  <input className="item-input" style={{width:'100%',boxSizing:'border-box'}} type="tel"
                    placeholder="(48) 99999-9999"
                    value={clientForm.phone}
                    onChange={(e) => setClientForm({...clientForm, phone:e.target.value})} />
                </div>
                <div>
                  <label style={{fontSize:'13px',fontWeight:700,display:'block',marginBottom:'6px'}}>E-mail</label>
                  <input className="item-input" style={{width:'100%',boxSizing:'border-box'}} type="email"
                    placeholder="cliente@email.com"
                    value={clientForm.email}
                    onChange={(e) => setClientForm({...clientForm, email:e.target.value})} />
                </div>
              </div>

              <div style={{borderTop:'1px solid #e6e2da',paddingTop:'14px'}}>
                <div style={{fontSize:'15px',fontWeight:900,marginBottom:'10px',color:'#655f51'}}>Ficha Bion — Prótese Capilar</div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(190px,1fr))',gap:'12px'}}>
                  <div>
                    <label style={{fontSize:'13px',fontWeight:700,display:'block',marginBottom:'6px'}}>Data de nascimento</label>
                    <input className="item-input" style={{width:'100%',boxSizing:'border-box'}} type="date"
                      value={clientForm.birth_date}
                      onChange={(e) => setClientForm({...clientForm, birth_date:e.target.value})} />
                  </div>
                  <div>
                    <label style={{fontSize:'13px',fontWeight:700,display:'block',marginBottom:'6px'}}>Modelo / tipo de prótese</label>
                    <input className="item-input" style={{width:'100%',boxSizing:'border-box'}}
                      placeholder="Ex.: Full lace, skin, híbrida..."
                      value={clientForm.hair_system_model}
                      onChange={(e) => setClientForm({...clientForm, hair_system_model:e.target.value})} />
                  </div>
                  <div>
                    <label style={{fontSize:'13px',fontWeight:700,display:'block',marginBottom:'6px'}}>Cor</label>
                    <input className="item-input" style={{width:'100%',boxSizing:'border-box'}}
                      placeholder="Ex.: 1B, castanho escuro..."
                      value={clientForm.hair_color}
                      onChange={(e) => setClientForm({...clientForm, hair_color:e.target.value})} />
                  </div>
                  <div>
                    <label style={{fontSize:'13px',fontWeight:700,display:'block',marginBottom:'6px'}}>Tamanho / base</label>
                    <input className="item-input" style={{width:'100%',boxSizing:'border-box'}}
                      placeholder="Ex.: 20 x 25 cm"
                      value={clientForm.base_size}
                      onChange={(e) => setClientForm({...clientForm, base_size:e.target.value})} />
                  </div>
                  <div>
                    <label style={{fontSize:'13px',fontWeight:700,display:'block',marginBottom:'6px'}}>Tipo de cabelo</label>
                    <input className="item-input" style={{width:'100%',boxSizing:'border-box'}}
                      placeholder="Ex.: humano, liso, ondulado..."
                      value={clientForm.hair_type}
                      onChange={(e) => setClientForm({...clientForm, hair_type:e.target.value})} />
                  </div>
                  <div>
                    <label style={{fontSize:'13px',fontWeight:700,display:'block',marginBottom:'6px'}}>Data da instalação</label>
                    <input className="item-input" style={{width:'100%',boxSizing:'border-box'}} type="date"
                      value={clientForm.installation_date}
                      onChange={(e) => setClientForm({...clientForm, installation_date:e.target.value})} />
                  </div>
                  <div>
                    <label style={{fontSize:'13px',fontWeight:700,display:'block',marginBottom:'6px'}}>Última manutenção</label>
                    <input className="item-input" style={{width:'100%',boxSizing:'border-box'}} type="date"
                      value={clientForm.last_maintenance_date}
                      onChange={(e) => {
                        const last = e.target.value;
                        let next = clientForm.next_maintenance_date;
                        if (last && clientForm.maintenance_interval_days) {
                          const d = new Date(`${last}T12:00:00`);
                          d.setDate(d.getDate() + Number(clientForm.maintenance_interval_days));
                          next = d.toISOString().split('T')[0];
                        }
                        setClientForm({...clientForm, last_maintenance_date:last, next_maintenance_date:next});
                      }} />
                  </div>
                  <div>
                    <label style={{fontSize:'13px',fontWeight:700,display:'block',marginBottom:'6px'}}>Intervalo de manutenção (dias)</label>
                    <input className="item-input" style={{width:'100%',boxSizing:'border-box'}} type="number" min="1"
                      placeholder="Ex.: 15"
                      value={clientForm.maintenance_interval_days}
                      onChange={(e) => {
                        const interval = e.target.value;
                        let next = clientForm.next_maintenance_date;
                        if (clientForm.last_maintenance_date && interval) {
                          const d = new Date(`${clientForm.last_maintenance_date}T12:00:00`);
                          d.setDate(d.getDate() + Number(interval));
                          next = d.toISOString().split('T')[0];
                        }
                        setClientForm({...clientForm, maintenance_interval_days:interval, next_maintenance_date:next});
                      }} />
                  </div>
                  <div>
                    <label style={{fontSize:'13px',fontWeight:700,display:'block',marginBottom:'6px'}}>Próxima manutenção</label>
                    <input className="item-input" style={{width:'100%',boxSizing:'border-box'}} type="date"
                      value={clientForm.next_maintenance_date}
                      onChange={(e) => setClientForm({...clientForm, next_maintenance_date:e.target.value})} />
                  </div>
                </div>
                <div style={{marginTop:'12px'}}>
                  <label style={{fontSize:'13px',fontWeight:700,display:'block',marginBottom:'6px'}}>Observações técnicas</label>
                  <textarea className="item-input" style={{width:'100%',boxSizing:'border-box',minHeight:'85px',resize:'vertical'}}
                    placeholder="Densidade, acabamento, adesivo, recorte, particularidades da prótese..."
                    value={clientForm.technical_notes}
                    onChange={(e) => setClientForm({...clientForm, technical_notes:e.target.value})} />
                </div>
              </div>

              <div>
                <label style={{fontSize:'13px',fontWeight:700,display:'block',marginBottom:'6px'}}>Observações gerais</label>
                <textarea className="item-input" style={{width:'100%',boxSizing:'border-box',minHeight:'90px',resize:'vertical'}}
                  placeholder="Preferências, observações ou informações importantes..."
                  value={clientForm.notes}
                  onChange={(e) => setClientForm({...clientForm, notes:e.target.value})} />
              </div>

              <div style={{display:'flex',gap:'10px',flexWrap:'wrap'}}>
                <button className="btn-primary" style={{flex:'1'}} type="submit">
                  {editingClientId ? 'Salvar alterações' : 'Cadastrar Cliente'}
                </button>
                <button type="button"
                  onClick={() => { setShowClientForm(false); resetClientForm(); }}
                  style={{padding:'12px 18px',borderRadius:'9px',border:'1px solid #ccc',background:'#fff',fontWeight:700,cursor:'pointer'}}>
                  Cancelar
                </button>
              </div>
            </form>
          )}

          <div style={{marginTop:'18px',display:'grid',gap:'12px'}}>
            {filteredClients.length === 0 && (
              <div className="item-card" style={{display:'block',textAlign:'center',padding:'30px 20px'}}>
                <div style={{fontSize:'34px',marginBottom:'8px'}}>👤</div>
                <div style={{fontWeight:800}}>Nenhum cliente encontrado</div>
                <div style={{color:'#777',fontSize:'14px',marginTop:'5px'}}>Cadastre um novo cliente ou altere sua busca.</div>
              </div>
            )}

            {filteredClients.map(client => {
              const stats = getClientStats(client.id);
              const isOpen = selectedClientId === client.id;

              return (
                <div key={client.id} className="item-card" style={{display:'block',padding:'0',overflow:'hidden'}}>
                  <div style={{padding:'18px',display:'flex',gap:'16px',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap'}}>
                    <div style={{flex:'1',minWidth:'220px'}}>
                      <div className="item-title" style={{fontSize:'19px'}}>{client.name}</div>
                      <div className="item-subtitle" style={{marginTop:'5px'}}>
                        📱 {client.phone || 'Sem telefone'}
                        {client.email ? `  •  ✉️ ${client.email}` : ''}
                      </div>
                      <div style={{display:'flex',gap:'8px',flexWrap:'wrap',marginTop:'10px'}}>
                        <span style={{background:'#f5f1df',padding:'6px 9px',borderRadius:'999px',fontSize:'12px',fontWeight:700}}>
                          ✂️ {stats.completedCount} concluído{stats.completedCount === 1 ? '' : 's'}
                        </span>
                        <span style={{background:'#f3f3f3',padding:'6px 9px',borderRadius:'999px',fontSize:'12px',fontWeight:700}}>
                          📅 {stats.lastVisit ? new Date(`${stats.lastVisit}T12:00:00`).toLocaleDateString('pt-BR') : 'Sem visitas'}
                        </span>
                        <span style={{background:'#f3f3f3',padding:'6px 9px',borderRadius:'999px',fontSize:'12px',fontWeight:700}}>
                          💰 R$ {stats.totalSpent.toFixed(2)}
                        </span>
                        {client.next_maintenance_date && (
                          <span style={{background:'#eee9dd',padding:'6px 9px',borderRadius:'999px',fontSize:'12px',fontWeight:700,color:'#665d47'}}>
                            Manutenção: {new Date(`${client.next_maintenance_date}T12:00:00`).toLocaleDateString('pt-BR')}
                          </span>
                        )}
                      </div>
                    </div>

                    <div style={{display:'flex',gap:'8px',flexWrap:'wrap'}}>
                      <button type="button" onClick={() => setSelectedClientId(isOpen ? null : client.id)}
                        style={{border:'1px solid #bbb',background:'#fff',borderRadius:'8px',padding:'9px 12px',fontWeight:700,cursor:'pointer'}}>
                        {isOpen ? 'Fechar ficha' : 'Ver ficha'}
                      </button>
                      <button type="button" onClick={() => openClientAppointment(client)}
                        style={{border:0,background:'#d4af37',borderRadius:'8px',padding:'9px 12px',fontWeight:800,cursor:'pointer'}}>
                        + Agendar
                      </button>
                      <button type="button" onClick={() => openEditClient(client)}
                        style={{border:'1px solid #bbb',background:'#fff',borderRadius:'8px',padding:'9px 12px',fontWeight:700,cursor:'pointer'}}>
                        Editar
                      </button>
                    </div>
                  </div>

                  {isOpen && (
                    <div style={{borderTop:'1px solid #e6e6e6',background:'#fafafa',padding:'18px'}}>
                      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))',gap:'10px',marginBottom:'18px'}}>
                        <div style={{background:'#fff',border:'1px solid #e3e3e3',borderRadius:'10px',padding:'14px'}}>
                          <div style={{fontSize:'12px',color:'#777'}}>Atendimentos concluídos</div>
                          <div style={{fontSize:'24px',fontWeight:900,marginTop:'4px'}}>{stats.completedCount}</div>
                        </div>
                        <div style={{background:'#fff',border:'1px solid #e3e3e3',borderRadius:'10px',padding:'14px'}}>
                          <div style={{fontSize:'12px',color:'#777'}}>Total gasto</div>
                          <div style={{fontSize:'24px',fontWeight:900,marginTop:'4px'}}>R$ {stats.totalSpent.toFixed(2)}</div>
                        </div>
                        <div style={{background:'#fff',border:'1px solid #e3e3e3',borderRadius:'10px',padding:'14px'}}>
                          <div style={{fontSize:'12px',color:'#777'}}>Última visita</div>
                          <div style={{fontSize:'18px',fontWeight:900,marginTop:'7px'}}>
                            {stats.lastVisit ? new Date(`${stats.lastVisit}T12:00:00`).toLocaleDateString('pt-BR') : '—'}
                          </div>
                        </div>
                      </div>

                      <div style={{background:'#fff',border:'1px solid #dedbd4',borderRadius:'12px',padding:'16px',marginBottom:'18px'}}>
                        <div style={{fontWeight:900,fontSize:'16px',marginBottom:'12px',color:'#655f51'}}>Ficha Bion — Prótese Capilar</div>
                        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(170px,1fr))',gap:'12px'}}>
                          <div><div style={{fontSize:'11px',color:'#777'}}>Nascimento</div><strong>{client.birth_date ? new Date(`${client.birth_date}T12:00:00`).toLocaleDateString('pt-BR') : '—'}</strong></div>
                          <div><div style={{fontSize:'11px',color:'#777'}}>Modelo / prótese</div><strong>{client.hair_system_model || '—'}</strong></div>
                          <div><div style={{fontSize:'11px',color:'#777'}}>Cor</div><strong>{client.hair_color || '—'}</strong></div>
                          <div><div style={{fontSize:'11px',color:'#777'}}>Base</div><strong>{client.base_size || '—'}</strong></div>
                          <div><div style={{fontSize:'11px',color:'#777'}}>Tipo de cabelo</div><strong>{client.hair_type || '—'}</strong></div>
                          <div><div style={{fontSize:'11px',color:'#777'}}>Instalação</div><strong>{client.installation_date ? new Date(`${client.installation_date}T12:00:00`).toLocaleDateString('pt-BR') : '—'}</strong></div>
                          <div><div style={{fontSize:'11px',color:'#777'}}>Última manutenção</div><strong>{client.last_maintenance_date ? new Date(`${client.last_maintenance_date}T12:00:00`).toLocaleDateString('pt-BR') : '—'}</strong></div>
                          <div><div style={{fontSize:'11px',color:'#777'}}>Próxima manutenção</div><strong>{client.next_maintenance_date ? new Date(`${client.next_maintenance_date}T12:00:00`).toLocaleDateString('pt-BR') : '—'}</strong></div>
                          <div><div style={{fontSize:'11px',color:'#777'}}>Intervalo</div><strong>{client.maintenance_interval_days ? `${client.maintenance_interval_days} dias` : '—'}</strong></div>
                        </div>
                        {client.technical_notes && (
                          <div style={{marginTop:'14px',paddingTop:'12px',borderTop:'1px solid #eee9df'}}>
                            <div style={{fontSize:'11px',color:'#777',marginBottom:'4px'}}>Observações técnicas</div>
                            <div style={{whiteSpace:'pre-wrap'}}>{client.technical_notes}</div>
                          </div>
                        )}
                      </div>

                      {client.notes && (
                        <div style={{background:'#fff8dc',border:'1px solid #eadb9a',borderRadius:'10px',padding:'12px',marginBottom:'18px'}}>
                          <strong>Observações:</strong> {client.notes}
                        </div>
                      )}

                      <div style={{fontWeight:900,fontSize:'16px',marginBottom:'10px'}}>Histórico</div>

                      {stats.history.length === 0 ? (
                        <div style={{color:'#777'}}>Nenhum agendamento registrado para este cliente.</div>
                      ) : (
                        <div style={{display:'grid',gap:'8px'}}>
                          {stats.history.map(apt => {
                            const service = services.find(s => s.id === apt.service_id);
                            return (
                              <div key={apt.id} style={{background:'#fff',border:'1px solid #e4e4e4',borderRadius:'9px',padding:'11px 12px',display:'flex',justifyContent:'space-between',gap:'12px',alignItems:'center',flexWrap:'wrap'}}>
                                <div>
                                  <strong>{new Date(`${apt.appointment_date}T12:00:00`).toLocaleDateString('pt-BR')}</strong>
                                  {' • '}{(apt.start_time || '').slice(0,5)}
                                  <div style={{fontSize:'13px',color:'#666',marginTop:'3px'}}>
                                    {service?.name || 'Serviço'} • R$ {Number(service?.price || 0).toFixed(2)}
                                  </div>
                                </div>
                                <span style={{padding:'6px 9px',borderRadius:'999px',background:statusColor(apt.status),color:'#fff',fontSize:'11px',fontWeight:800}}>
                                  {statusLabel(apt.status)}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      <div style={{display:'flex',gap:'8px',marginTop:'16px',flexWrap:'wrap'}}>
                        <button type="button" onClick={() => openClientAppointment(client)}
                          style={{border:0,background:'#d4af37',borderRadius:'8px',padding:'10px 14px',fontWeight:800,cursor:'pointer'}}>
                          + Novo agendamento
                        </button>
                        <button type="button" onClick={() => deleteClient(client.id)}
                          style={{border:'1px solid #e06a6a',background:'#fff',color:'#b52d2d',borderRadius:'8px',padding:'10px 14px',fontWeight:700,cursor:'pointer'}}>
                          Excluir cliente
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Services 2.0 */}
      {screen === 'services' && (
        <div className="screen-content">
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(155px,1fr))',gap:'12px',marginBottom:'16px'}}>
            <div className="item-card" style={{display:'block'}}>
              <div className="stat-label">Serviços cadastrados</div>
              <div style={{fontSize:'24px',fontWeight:900,marginTop:'7px'}}>{services.length}</div>
            </div>
            <div className="item-card" style={{display:'block'}}>
              <div className="stat-label">Serviços ativos</div>
              <div style={{fontSize:'24px',fontWeight:900,marginTop:'7px'}}>{activeServicesCount}</div>
            </div>
            <div className="item-card" style={{display:'block',borderLeft:'5px solid #d4af37'}}>
              <div className="stat-label">Preço médio</div>
              <div style={{fontSize:'24px',fontWeight:900,marginTop:'7px'}}>{money(averageServicePrice)}</div>
            </div>
          </div>

          <div style={{display:'flex',gap:'10px',flexWrap:'wrap'}}>
            <input
              className="item-input"
              style={{flex:'2',minWidth:'220px',boxSizing:'border-box'}}
              type="search"
              placeholder="🔎 Buscar serviço..."
              value={serviceSearch}
              onChange={(e) => setServiceSearch(e.target.value)}
            />
            <button className="btn-primary" style={{flex:'1',minWidth:'190px'}} onClick={openNewService}>
              + Novo Serviço
            </button>
          </div>

          {showServiceForm && (
            <form onSubmit={saveService} className="item-card" style={{display:'grid',gap:'14px',marginTop:'16px',alignItems:'stretch'}}>
              <div style={{fontSize:'20px',fontWeight:900}}>
                {editingServiceId ? 'Editar serviço' : 'Novo serviço'}
              </div>

              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))',gap:'12px'}}>
                <div>
                  <label style={{display:'block',fontSize:'13px',fontWeight:700,marginBottom:'6px'}}>Nome *</label>
                  <input className="item-input" style={{width:'100%',boxSizing:'border-box'}} required
                    placeholder="Ex.: Corte masculino"
                    value={serviceForm.name}
                    onChange={(e)=>setServiceForm({...serviceForm,name:e.target.value})}/>
                </div>
                <div>
                  <label style={{display:'block',fontSize:'13px',fontWeight:700,marginBottom:'6px'}}>Preço *</label>
                  <input className="item-input" style={{width:'100%',boxSizing:'border-box'}} required inputMode="decimal"
                    placeholder="50,00"
                    value={serviceForm.price}
                    onChange={(e)=>setServiceForm({...serviceForm,price:e.target.value})}/>
                </div>
                <div>
                  <label style={{display:'block',fontSize:'13px',fontWeight:700,marginBottom:'6px'}}>Duração (min) *</label>
                  <input className="item-input" style={{width:'100%',boxSizing:'border-box'}} type="number" min="5" step="5" required
                    value={serviceForm.duration_minutes}
                    onChange={(e)=>setServiceForm({...serviceForm,duration_minutes:e.target.value})}/>
                </div>
              </div>

              <label style={{display:'flex',gap:'9px',alignItems:'center',fontWeight:700,cursor:'pointer'}}>
                <input type="checkbox" checked={serviceForm.active}
                  onChange={(e)=>setServiceForm({...serviceForm,active:e.target.checked})}/>
                Serviço ativo e disponível para novos agendamentos
              </label>

              <div style={{display:'flex',gap:'10px',flexWrap:'wrap'}}>
                <button className="btn-primary" type="submit" style={{flex:'1'}}>
                  {editingServiceId ? 'Salvar alterações' : 'Cadastrar Serviço'}
                </button>
                <button type="button" onClick={()=>{setShowServiceForm(false);resetServiceForm();}}
                  style={{padding:'12px 18px',borderRadius:'9px',border:'1px solid #ccc',background:'#fff',fontWeight:700,cursor:'pointer'}}>
                  Cancelar
                </button>
              </div>
            </form>
          )}

          <div style={{fontSize:'13px',color:'#777',marginTop:'14px'}}>
            {filteredServices.length} {filteredServices.length === 1 ? 'serviço encontrado' : 'serviços encontrados'}
          </div>

          <div style={{marginTop:'12px',display:'grid',gap:'10px'}}>
            {filteredServices.length === 0 ? (
              <div className="item-card" style={{display:'block',textAlign:'center',padding:'30px 20px'}}>
                <div style={{fontSize:'34px'}}>✂️</div>
                <div style={{fontWeight:900,marginTop:'6px'}}>Nenhum serviço encontrado</div>
                <div style={{color:'#777',fontSize:'14px',marginTop:'5px'}}>Cadastre um serviço ou altere sua busca.</div>
              </div>
            ) : filteredServices.map(service => (
              <div key={service.id} className="item-card" style={{borderLeft:`5px solid ${service.active === false ? '#999' : '#d4af37'}`}}>
                <div style={{flex:'1',minWidth:'200px'}}>
                  <div style={{display:'flex',gap:'8px',alignItems:'center',flexWrap:'wrap'}}>
                    <div className="item-title" style={{fontSize:'18px'}}>{service.name}</div>
                    <span style={{
                      padding:'5px 8px',borderRadius:'999px',fontSize:'11px',fontWeight:800,
                      background:service.active === false ? '#eee' : '#f5f1df',
                      color:service.active === false ? '#666' : '#7a620d'
                    }}>
                      {service.active === false ? 'Inativo' : 'Ativo'}
                    </span>
                  </div>
                  <div className="item-subtitle" style={{marginTop:'6px'}}>
                    {money(service.price)} • ⏱️ {service.duration_minutes || 30} min
                  </div>
                </div>

                <div className="item-actions" style={{display:'flex',gap:'8px',alignItems:'center',flexWrap:'wrap'}}>
                  <button type="button" onClick={() => toggleServiceActive(service)}
                    style={{border:'1px solid #bbb',background:'#fff',borderRadius:'8px',padding:'9px 12px',fontWeight:700,cursor:'pointer'}}>
                    {service.active === false ? 'Ativar' : 'Desativar'}
                  </button>
                  <button type="button" onClick={() => openEditService(service)}
                    style={{border:'1px solid #bbb',background:'#fff',borderRadius:'8px',padding:'9px 12px',fontWeight:700,cursor:'pointer'}}>
                    Editar
                  </button>
                  <button className="btn-delete" onClick={() => deleteService(service.id)}>✕</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Cash 2.0 */}
      {screen === 'cash' && (
        <div className="screen-content">
          <div style={{display:'flex',gap:'10px',flexWrap:'wrap',marginBottom:'16px'}}>
            <button className="btn-primary" style={{flex:'1',minWidth:'190px'}} onClick={() => openCashForm('income')}>
              + Nova Entrada
            </button>
            <button type="button" style={{flex:'1',minWidth:'190px',padding:'12px',border:'1px solid #d8d8d8',borderRadius:'9px',background:'#fff',fontWeight:800,cursor:'pointer'}}
              onClick={() => openCashForm('expense')}>
              − Nova Despesa
            </button>
          </div>

          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(155px,1fr))',gap:'12px',marginBottom:'18px'}}>
            <div className="item-card" style={{display:'block'}}>
              <div className="stat-label">Entradas hoje</div>
              <div style={{fontSize:'24px',fontWeight:900,marginTop:'7px'}}>{money(todayIncome)}</div>
            </div>
            <div className="item-card" style={{display:'block'}}>
              <div className="stat-label">Entradas no mês</div>
              <div style={{fontSize:'24px',fontWeight:900,marginTop:'7px'}}>{money(monthIncome)}</div>
            </div>
            <div className="item-card" style={{display:'block'}}>
              <div className="stat-label">Despesas no mês</div>
              <div style={{fontSize:'24px',fontWeight:900,marginTop:'7px',color:'#b83232'}}>{money(monthExpenses)}</div>
            </div>
            <div className="item-card" style={{display:'block',borderLeft:'5px solid #d4af37'}}>
              <div className="stat-label">Saldo do mês</div>
              <div style={{fontSize:'24px',fontWeight:900,marginTop:'7px'}}>{money(monthBalance)}</div>
            </div>
          </div>

          {showCashForm && (
            <form onSubmit={saveCash} className="item-card" style={{display:'grid',gap:'14px',marginBottom:'18px',alignItems:'stretch'}}>
              <div style={{display:'flex',justifyContent:'space-between',gap:'12px',alignItems:'center',flexWrap:'wrap'}}>
                <div style={{fontSize:'20px',fontWeight:900}}>
                  {cashForm.type === 'income' ? '💰 Nova entrada' : '📉 Nova despesa'}
                </div>
                <div style={{display:'flex',gap:'6px'}}>
                  <button type="button" onClick={() => setCashForm({...cashForm,type:'income',category:'Serviço'})}
                    style={{padding:'8px 12px',borderRadius:'8px',border:'1px solid #d4af37',background:cashForm.type==='income'?'#d4af37':'#fff',fontWeight:800,cursor:'pointer'}}>
                    Entrada
                  </button>
                  <button type="button" onClick={() => setCashForm({...cashForm,type:'expense',category:'Despesa geral'})}
                    style={{padding:'8px 12px',borderRadius:'8px',border:'1px solid #bbb',background:cashForm.type==='expense'?'#222':'#fff',color:cashForm.type==='expense'?'#fff':'#111',fontWeight:800,cursor:'pointer'}}>
                    Despesa
                  </button>
                </div>
              </div>

              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(190px,1fr))',gap:'12px'}}>
                <div>
                  <label style={{display:'block',fontSize:'13px',fontWeight:700,marginBottom:'6px'}}>Descrição *</label>
                  <input className="item-input" style={{width:'100%',boxSizing:'border-box'}} required
                    placeholder={cashForm.type === 'income' ? 'Ex.: Corte João' : 'Ex.: Compra de produtos'}
                    value={cashForm.description}
                    onChange={(e)=>setCashForm({...cashForm,description:e.target.value})}/>
                </div>
                <div>
                  <label style={{display:'block',fontSize:'13px',fontWeight:700,marginBottom:'6px'}}>Valor *</label>
                  <input className="item-input" style={{width:'100%',boxSizing:'border-box'}} required inputMode="decimal"
                    placeholder="0,00"
                    value={cashForm.amount}
                    onChange={(e)=>setCashForm({...cashForm,amount:e.target.value})}/>
                </div>
                <div>
                  <label style={{display:'block',fontSize:'13px',fontWeight:700,marginBottom:'6px'}}>Data *</label>
                  <input className="item-input" style={{width:'100%',boxSizing:'border-box'}} type="date" required
                    value={cashForm.date}
                    onChange={(e)=>setCashForm({...cashForm,date:e.target.value})}/>
                </div>
                <div>
                  <label style={{display:'block',fontSize:'13px',fontWeight:700,marginBottom:'6px'}}>Pagamento</label>
                  <select className="item-input" style={{width:'100%',boxSizing:'border-box'}}
                    value={cashForm.payment_method}
                    onChange={(e)=>setCashForm({...cashForm,payment_method:e.target.value})}>
                    <option value="pix">Pix</option>
                    <option value="cash">Dinheiro</option>
                    <option value="debit">Débito</option>
                    <option value="credit">Crédito</option>
                  </select>
                </div>
                <div>
                  <label style={{display:'block',fontSize:'13px',fontWeight:700,marginBottom:'6px'}}>Categoria</label>
                  <select className="item-input" style={{width:'100%',boxSizing:'border-box'}}
                    value={cashForm.category}
                    onChange={(e)=>setCashForm({...cashForm,category:e.target.value})}>
                    {cashForm.type === 'income' ? (
                      <>
                        <option>Serviço</option>
                        <option>Produto</option>
                        <option>Outras entradas</option>
                      </>
                    ) : (
                      <>
                        <option>Despesa geral</option>
                        <option>Estoque</option>
                        <option>Aluguel</option>
                        <option>Água / Luz / Internet</option>
                        <option>Manutenção</option>
                        <option>Outras despesas</option>
                      </>
                    )}
                  </select>
                </div>
              </div>

              <div>
                <label style={{display:'block',fontSize:'13px',fontWeight:700,marginBottom:'6px'}}>Observações</label>
                <textarea className="item-input" style={{width:'100%',boxSizing:'border-box',minHeight:'70px',resize:'vertical'}}
                  placeholder="Opcional"
                  value={cashForm.notes}
                  onChange={(e)=>setCashForm({...cashForm,notes:e.target.value})}/>
              </div>

              <div style={{display:'flex',gap:'10px',flexWrap:'wrap'}}>
                <button className="btn-primary" type="submit" style={{flex:'1'}}>Salvar movimento</button>
                <button type="button" onClick={()=>setShowCashForm(false)}
                  style={{padding:'12px 18px',borderRadius:'9px',border:'1px solid #ccc',background:'#fff',fontWeight:700,cursor:'pointer'}}>
                  Cancelar
                </button>
              </div>
            </form>
          )}

          <div className="item-card" style={{display:'block',marginBottom:'14px'}}>
            <div style={{display:'flex',gap:'10px',alignItems:'center',flexWrap:'wrap'}}>
              <input className="item-input" type="search" placeholder="🔎 Buscar no caixa..."
                style={{flex:'2',minWidth:'200px',boxSizing:'border-box'}}
                value={cashSearch} onChange={(e)=>setCashSearch(e.target.value)}/>
              <select className="item-input" style={{flex:'1',minWidth:'150px'}}
                value={cashFilter} onChange={(e)=>setCashFilter(e.target.value)}>
                <option value="today">Hoje</option>
                <option value="month">Este mês</option>
                <option value="all">Todo o período</option>
              </select>
            </div>
          </div>

          <div style={{display:'grid',gap:'10px'}}>
            {filteredCash.length === 0 ? (
              <div className="item-card" style={{display:'block',textAlign:'center',padding:'30px 20px'}}>
                <div style={{fontSize:'32px'}}>💵</div>
                <div style={{fontWeight:900,marginTop:'6px'}}>Nenhum movimento encontrado</div>
              </div>
            ) : filteredCash.map(item => {
              const isExpense = item.type === 'expense';
              return (
                <div key={item.id} className="item-card" style={{borderLeft:`5px solid ${isExpense ? '#c94b4b' : '#2e9d62'}`}}>
                  <div style={{flex:'1',minWidth:'190px'}}>
                    <div className="item-title">{item.description}</div>
                    <div className="item-subtitle" style={{marginTop:'5px'}}>
                      {new Date(`${item.date}T12:00:00`).toLocaleDateString('pt-BR')}
                      {' • '}{item.category || (isExpense ? 'Despesa' : 'Entrada')}
                      {' • '}{paymentLabel(item.payment_method)}
                    </div>
                    {item.notes && <div style={{fontSize:'12px',color:'#777',marginTop:'5px'}}>{item.notes}</div>}
                  </div>
                  <div className="item-actions">
                    <span style={{fontSize:'18px',fontWeight:900,color:isExpense?'#b83232':'#247a4c'}}>
                      {isExpense ? '− ' : '+ '}{money(item.amount)}
                    </span>
                    <button className="btn-delete" onClick={() => deleteCash(item.id)}>✕</button>
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{marginTop:'16px',fontSize:'13px',color:'#777',textAlign:'center'}}>
            Saldo geral registrado: <strong>{money(totalCash)}</strong>
          </div>
        </div>
      )}

      {/* Estoque 3.0 */}
      {screen === 'inventory' && (
        <div className="screen-content">
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(155px,1fr))',gap:'12px',marginBottom:'16px'}}>
            <div className="item-card" style={{display:'block'}}><div className="stat-label">Produtos</div><div style={{fontSize:'24px',fontWeight:900,marginTop:'7px'}}>{inventory.length}</div></div>
            <div className="item-card" style={{display:'block'}}><div className="stat-label">Estoque baixo</div><div style={{fontSize:'24px',fontWeight:900,marginTop:'7px',color:lowStockCount?'#b83232':'inherit'}}>{lowStockCount}</div></div>
            <div className="item-card" style={{display:'block'}}><div className="stat-label">Custo em estoque</div><div style={{fontSize:'24px',fontWeight:900,marginTop:'7px'}}>{money(inventoryCostValue)}</div></div>
            <div className="item-card" style={{display:'block',borderLeft:'5px solid #d4af37'}}><div className="stat-label">Valor de venda</div><div style={{fontSize:'24px',fontWeight:900,marginTop:'7px'}}>{money(inventorySaleValue)}</div></div>
          </div>

          <div style={{display:'flex',gap:'10px',flexWrap:'wrap'}}>
            <input className="item-input" type="search" placeholder="🔎 Buscar produto..." style={{flex:'2',minWidth:'220px',boxSizing:'border-box'}} value={inventorySearch} onChange={e=>setInventorySearch(e.target.value)}/>
            <button className="btn-primary" style={{flex:'1',minWidth:'190px'}} onClick={openNewInventory}>+ Novo Produto</button>
          </div>

          {showInventoryForm && (
            <form onSubmit={saveInventory} className="item-card" style={{display:'grid',gap:'14px',marginTop:'16px',alignItems:'stretch'}}>
              <div style={{fontSize:'20px',fontWeight:900}}>{editingInventoryId ? 'Editar produto' : 'Novo produto'}</div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))',gap:'12px'}}>
                <input className="item-input" required placeholder="Nome do produto" value={inventoryForm.name} onChange={e=>setInventoryForm({...inventoryForm,name:e.target.value})}/>
                <input className="item-input" type="number" min="0" placeholder="Quantidade" value={inventoryForm.quantity} onChange={e=>setInventoryForm({...inventoryForm,quantity:e.target.value})}/>
                <input className="item-input" type="number" min="0" placeholder="Estoque mínimo" value={inventoryForm.min_quantity} onChange={e=>setInventoryForm({...inventoryForm,min_quantity:e.target.value})}/>
                <input className="item-input" inputMode="decimal" placeholder="Custo unitário" value={inventoryForm.cost_price} onChange={e=>setInventoryForm({...inventoryForm,cost_price:e.target.value})}/>
                <input className="item-input" inputMode="decimal" placeholder="Preço de venda" value={inventoryForm.sale_price} onChange={e=>setInventoryForm({...inventoryForm,sale_price:e.target.value})}/>
                <select className="item-input" value={inventoryForm.unit} onChange={e=>setInventoryForm({...inventoryForm,unit:e.target.value})}>
                  <option value="un">Unidade</option><option value="ml">ml</option><option value="g">g</option><option value="kg">kg</option>
                </select>
              </div>
              <div style={{display:'flex',gap:'10px'}}>
                <button className="btn-primary" type="submit" style={{flex:1}}>Salvar Produto</button>
                <button type="button" onClick={()=>{setShowInventoryForm(false);resetInventoryForm();}} style={{padding:'12px 18px',borderRadius:'9px',border:'1px solid #ccc',background:'#fff',fontWeight:700}}>Cancelar</button>
              </div>
            </form>
          )}

          {showInventoryMovementForm && (() => {
            const item = inventory.find(i => String(i.id) === String(inventoryMovementForm.inventory_id));
            const total = Number(inventoryMovementForm.quantity || 0) * Number(String(inventoryMovementForm.unit_price || 0).replace(',', '.'));
            const type = inventoryMovementForm.movement_type;
            return (
              <form onSubmit={saveInventoryMovement} className="item-card" style={{display:'grid',gap:'14px',marginTop:'16px',alignItems:'stretch',borderLeft:'5px solid #d4af37'}}>
                <div>
                  <div style={{fontSize:'20px',fontWeight:900}}>{inventoryMovementLabel(type)} — {item?.name || 'Produto'}</div>
                  <div style={{fontSize:'13px',color:'#777',marginTop:'4px'}}>Estoque atual: {item?.quantity || 0} {item?.unit || 'un'}</div>
                </div>

                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(170px,1fr))',gap:'12px'}}>
                  <div>
                    <label style={{display:'block',fontSize:'13px',fontWeight:700,marginBottom:'6px'}}>Tipo</label>
                    <select className="item-input" style={{width:'100%',boxSizing:'border-box'}} value={type}
                      onChange={e=>{
                        const newType=e.target.value;
                        setInventoryMovementForm({...inventoryMovementForm,movement_type:newType,unit_price:newType==='sale'?String(item?.sale_price||''):String(item?.cost_price||'')});
                      }}>
                      <option value="sale">Venda</option>
                      <option value="restock">Reposição</option>
                      <option value="internal_use">Uso interno</option>
                      <option value="loss">Perda / Ajuste</option>
                    </select>
                  </div>
                  <div>
                    <label style={{display:'block',fontSize:'13px',fontWeight:700,marginBottom:'6px'}}>Quantidade</label>
                    <input className="item-input" style={{width:'100%',boxSizing:'border-box'}} type="number" min="1" required value={inventoryMovementForm.quantity}
                      onChange={e=>setInventoryMovementForm({...inventoryMovementForm,quantity:e.target.value})}/>
                  </div>
                  <div>
                    <label style={{display:'block',fontSize:'13px',fontWeight:700,marginBottom:'6px'}}>{type==='sale'?'Preço unitário':'Custo unitário'}</label>
                    <input className="item-input" style={{width:'100%',boxSizing:'border-box'}} inputMode="decimal" value={inventoryMovementForm.unit_price}
                      onChange={e=>setInventoryMovementForm({...inventoryMovementForm,unit_price:e.target.value})}/>
                  </div>
                  {(type==='sale' || type==='restock') && (
                    <div>
                      <label style={{display:'block',fontSize:'13px',fontWeight:700,marginBottom:'6px'}}>Pagamento</label>
                      <select className="item-input" style={{width:'100%',boxSizing:'border-box'}} value={inventoryMovementForm.payment_method}
                        onChange={e=>setInventoryMovementForm({...inventoryMovementForm,payment_method:e.target.value})}>
                        <option value="pix">Pix</option><option value="cash">Dinheiro</option><option value="debit">Débito</option><option value="credit">Crédito</option>
                      </select>
                    </div>
                  )}
                </div>

                <textarea className="item-input" style={{width:'100%',boxSizing:'border-box',minHeight:'65px'}} placeholder="Observação (opcional)"
                  value={inventoryMovementForm.notes} onChange={e=>setInventoryMovementForm({...inventoryMovementForm,notes:e.target.value})}/>

                {(type==='sale' || type==='restock') && (
                  <div style={{background:type==='sale'?'#edf8f2':'#fff6e5',padding:'12px',borderRadius:'10px',fontWeight:900}}>
                    {type==='sale'?'Entrada automática no Caixa':'Despesa automática no Caixa'}: {money(total)}
                  </div>
                )}

                <div style={{display:'flex',gap:'10px',flexWrap:'wrap'}}>
                  <button className="btn-primary" type="submit" style={{flex:1}}>Confirmar {inventoryMovementLabel(type)}</button>
                  <button type="button" onClick={()=>{setShowInventoryMovementForm(false);resetInventoryMovementForm();}}
                    style={{padding:'12px 18px',borderRadius:'9px',border:'1px solid #ccc',background:'#fff',fontWeight:700}}>Cancelar</button>
                </div>
              </form>
            );
          })()}

          <div style={{display:'grid',gap:'10px',marginTop:'16px'}}>
            {filteredInventory.length === 0 ? (
              <div className="item-card" style={{display:'block',textAlign:'center',padding:'30px'}}><div style={{fontSize:'34px'}}>📦</div><div style={{fontWeight:900}}>Nenhum produto cadastrado</div></div>
            ) : filteredInventory.map(item => {
              const low = Number(item.quantity||0) <= Number(item.min_quantity||0);
              return (
                <div className="item-card" key={item.id} style={{borderLeft:`5px solid ${low?'#c94b4b':'#d4af37'}`}}>
                  <div style={{flex:1,minWidth:'210px'}}>
                    <div className="item-title">{item.name}</div>
                    <div className="item-subtitle">{item.quantity} {item.unit || 'un'} em estoque • mínimo {item.min_quantity || 0}</div>
                    <div className="item-subtitle">Custo {money(item.cost_price)} • Venda {money(item.sale_price)}</div>
                    {low && <div style={{fontSize:'12px',fontWeight:800,color:'#b83232',marginTop:'5px'}}>⚠️ Estoque baixo</div>}
                  </div>
                  <div className="item-actions" style={{display:'flex',gap:'7px',flexWrap:'wrap'}}>
                    <button onClick={()=>openInventoryMovement(item,'sale')} style={{padding:'9px 12px',border:0,borderRadius:'8px',background:'#2e9d62',color:'#fff',fontWeight:800}}>Vender</button>
                    <button onClick={()=>openInventoryMovement(item,'restock')} style={{padding:'9px 12px',border:'1px solid #d4af37',borderRadius:'8px',background:'#fff',fontWeight:800}}>Repor</button>
                    <button onClick={()=>openInventoryMovement(item,'internal_use')} style={{padding:'9px 12px',border:'1px solid #bbb',borderRadius:'8px',background:'#fff',fontWeight:700}}>Saída</button>
                    <button onClick={()=>openEditInventory(item)} style={{padding:'9px 12px',border:'1px solid #bbb',borderRadius:'8px',background:'#fff',fontWeight:700}}>Editar</button>
                    <button className="btn-delete" onClick={()=>deleteInventory(item.id)}>✕</button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="item-card" style={{display:'block',marginTop:'20px'}}>
            <div style={{fontSize:'18px',fontWeight:900,marginBottom:'12px'}}>📋 Histórico de movimentações</div>
            {inventoryMovements.length===0 ? (
              <div style={{color:'#777'}}>Nenhuma movimentação registrada ainda.</div>
            ) : (
              <div style={{display:'grid',gap:'8px'}}>
                {inventoryMovements.slice(0,30).map(movement=>{
                  const item=inventory.find(i=>i.id===movement.inventory_id);
                  const isEntry=movement.movement_type==='restock';
                  const qty=Number(movement.quantity||0);
                  const total=qty*Number(movement.unit_price||0);
                  return (
                    <div key={movement.id} style={{border:'1px solid #e4e4e4',borderRadius:'9px',padding:'11px 12px',display:'flex',justifyContent:'space-between',gap:'12px',alignItems:'center',flexWrap:'wrap'}}>
                      <div>
                        <strong>{inventoryMovementLabel(movement.movement_type)} • {item?.name || 'Produto'}</strong>
                        <div style={{fontSize:'13px',color:'#666',marginTop:'3px'}}>
                          {new Date(movement.created_at).toLocaleString('pt-BR')} • {qty} {item?.unit || 'un'}
                          {movement.movement_type==='sale' ? ` • ${paymentLabel(movement.payment_method)}` : ''}
                        </div>
                        {movement.notes && <div style={{fontSize:'12px',color:'#777',marginTop:'3px'}}>{movement.notes}</div>}
                      </div>
                      <div style={{fontWeight:900,color:isEntry?'#247a4c':movement.movement_type==='sale'?'#247a4c':'#b36b00'}}>
                        {isEntry?'+':'−'} {qty} {item?.unit || 'un'}
                        {(movement.movement_type==='sale'||movement.movement_type==='restock') && <div style={{fontSize:'12px',color:'#555',textAlign:'right'}}>{money(total)}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}


      {/* Relatórios 1.0 */}
      {screen === 'reports' && (() => {
        const now = new Date();
        const startOfWeek = new Date(now);
        const day = startOfWeek.getDay();
        const diff = day === 0 ? -6 : 1 - day;
        startOfWeek.setDate(startOfWeek.getDate() + diff);
        startOfWeek.setHours(0,0,0,0);

        const inPeriod = (dateValue) => {
          if (!dateValue) return false;
          const d = new Date(`${String(dateValue).slice(0,10)}T12:00:00`);
          if (reportPeriod === 'today') return String(dateValue).slice(0,10) === today;
          if (reportPeriod === 'week') return d >= startOfWeek && d <= now;
          if (reportPeriod === 'month') return String(dateValue).slice(0,7) === currentMonth;
          return true;
        };

        const periodCash = cash.filter(item => inPeriod(item.date));
        const income = periodCash.filter(i => (i.type || 'income') === 'income');
        const expenses = periodCash.filter(i => i.type === 'expense');
        const revenue = income.reduce((s,i)=>s+Number(i.amount||0),0);
        const expenseTotal = expenses.reduce((s,i)=>s+Number(i.amount||0),0);
        const balance = revenue - expenseTotal;

        const serviceRevenue = income
          .filter(i => String(i.category || '').toLowerCase() === 'serviço' || String(i.category || '').toLowerCase() === 'servico')
          .reduce((s,i)=>s+Number(i.amount||0),0);
        const productRevenue = income
          .filter(i => String(i.category || '').toLowerCase() === 'produto')
          .reduce((s,i)=>s+Number(i.amount||0),0);

        const completed = appointments.filter(a => a.status === 'completed' && inPeriod(a.appointment_date));
        const ticket = completed.length ? serviceRevenue / completed.length : 0;

        const periodMovements = inventoryMovements.filter(m => inPeriod(m.created_at));
        const sales = periodMovements.filter(m => m.movement_type === 'sale');
        const unitsSold = sales.reduce((s,m)=>s+Number(m.quantity||0),0);
        const productSalesValue = sales.reduce((s,m)=>s+Number(m.quantity||0)*Number(m.unit_price||0),0);
        const estimatedProductCost = sales.reduce((s,m)=>{
          const item = inventory.find(i => i.id === m.inventory_id);
          return s + Number(m.quantity||0) * Number(item?.cost_price||0);
        },0);
        const estimatedProductProfit = productSalesValue - estimatedProductCost;

        const serviceCounts = {};
        completed.forEach(a => {
          const service = services.find(s => s.id === a.service_id);
          const name = service?.name || 'Serviço';
          serviceCounts[name] = (serviceCounts[name] || 0) + 1;
        });
        const topServices = Object.entries(serviceCounts).sort((a,b)=>b[1]-a[1]).slice(0,5);

        const productCounts = {};
        sales.forEach(m => {
          const item = inventory.find(i => i.id === m.inventory_id);
          const name = item?.name || 'Produto';
          productCounts[name] = (productCounts[name] || 0) + Number(m.quantity||0);
        });
        const topProducts = Object.entries(productCounts).sort((a,b)=>b[1]-a[1]).slice(0,5);

        const periodLabel = reportPeriod === 'today' ? 'Hoje' : reportPeriod === 'week' ? 'Esta semana' : reportPeriod === 'month' ? 'Este mês' : 'Todo o período';
        const chartDays = 14;
        const dailyRevenue = Array.from({length:chartDays},(_,idx)=>{
          const d=new Date(now); d.setDate(now.getDate()-(chartDays-1-idx));
          const key=d.toISOString().slice(0,10);
          const value=cash.filter(i=>String(i.date).slice(0,10)===key&&(i.type||'income')==='income').reduce((s,i)=>s+Number(i.amount||0),0);
          return {key,label:d.toLocaleDateString('pt-BR',{day:'2-digit',month:'2-digit'}),value};
        });
        const maxDailyRevenue=Math.max(1,...dailyRevenue.map(d=>d.value));
        const paymentTotals={};
        income.forEach(i=>{const label=paymentLabel(i.payment_method||'Não informado');paymentTotals[label]=(paymentTotals[label]||0)+Number(i.amount||0);});
        const paymentEntries=Object.entries(paymentTotals).sort((a,b)=>b[1]-a[1]);
        const maxPayment=Math.max(1,...paymentEntries.map(([,v])=>v));
        const previousMonthDate=new Date(now.getFullYear(),now.getMonth()-1,1);
        const previousMonthKey=`${previousMonthDate.getFullYear()}-${String(previousMonthDate.getMonth()+1).padStart(2,'0')}`;
        const previousMonthRevenue=cash.filter(i=>String(i.date).slice(0,7)===previousMonthKey&&(i.type||'income')==='income').reduce((s,i)=>s+Number(i.amount||0),0);
        const currentMonthRevenue=cash.filter(i=>String(i.date).slice(0,7)===currentMonth&&(i.type||'income')==='income').reduce((s,i)=>s+Number(i.amount||0),0);
        const monthVariation=previousMonthRevenue>0?((currentMonthRevenue-previousMonthRevenue)/previousMonthRevenue)*100:null;

        const downloadReportPDF = () => {
          const doc = new jsPDF({ unit: 'mm', format: 'a4' });
          const pageWidth = doc.internal.pageSize.getWidth();
          const margin = 16;
          let y = 18;

          const addPageIfNeeded = (needed = 18) => {
            if (y + needed > 282) {
              doc.addPage();
              y = 18;
            }
          };

          const line = () => {
            doc.setDrawColor(220, 216, 207);
            doc.line(margin, y, pageWidth - margin, y);
            y += 7;
          };

          const section = (title) => {
            addPageIfNeeded(18);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(13);
            doc.setTextColor(65, 62, 56);
            doc.text(title, margin, y);
            y += 7;
          };

          const row = (label, value, strong = false) => {
            addPageIfNeeded(8);
            doc.setFont('helvetica', strong ? 'bold' : 'normal');
            doc.setFontSize(10);
            doc.setTextColor(80, 80, 80);
            doc.text(String(label), margin, y);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(25, 25, 25);
            doc.text(String(value), pageWidth - margin, y, { align: 'right' });
            y += 6;
          };

          // Cabeçalho
          doc.setFillColor(28, 28, 28);
          doc.rect(0, 0, pageWidth, 35, 'F');
          doc.setTextColor(255, 255, 255);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(20);
          doc.text('BION', margin, 15);
          doc.setFontSize(8);
          doc.setTextColor(190, 185, 174);
          doc.text('PROTESE CAPILAR', margin, 21);
          doc.setFontSize(15);
          doc.setTextColor(255, 255, 255);
          doc.text('RELATORIO GERENCIAL', pageWidth - margin, 16, { align: 'right' });
          doc.setFontSize(9);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(205, 201, 193);
          doc.text(`Periodo: ${periodLabel}`, pageWidth - margin, 22, { align: 'right' });

          y = 46;
          doc.setTextColor(35, 35, 35);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(16);
          doc.text('Resumo financeiro', margin, y);
          y += 10;

          row('Faturamento', money(revenue), true);
          row('Despesas', money(expenseTotal), true);
          row('Saldo', money(balance), true);
          line();

          section('Atendimentos e vendas');
          row('Atendimentos concluidos', completed.length);
          row('Ticket medio de servicos', money(ticket));
          row('Produtos vendidos', `${unitsSold} un`);
          line();

          section('Receita por origem');
          row('Servicos', money(serviceRevenue));
          row('Produtos', money(productRevenue));
          row('Outras entradas', money(Math.max(0, revenue - serviceRevenue - productRevenue)));
          line();

          section('Resultado de produtos');
          row('Vendas registradas', money(productSalesValue));
          row('Custo estimado', money(estimatedProductCost));
          row('Lucro bruto estimado', money(estimatedProductProfit));
          line();

          section('Servicos mais realizados');
          if (topServices.length === 0) {
            row('Nenhum atendimento concluido no periodo', '');
          } else {
            topServices.forEach(([name, count], idx) => row(`${idx + 1}. ${name}`, count));
          }
          line();

          section('Produtos mais vendidos');
          if (topProducts.length === 0) {
            row('Nenhuma venda de produto no periodo', '');
          } else {
            topProducts.forEach(([name, count], idx) => row(`${idx + 1}. ${name}`, `${count} un`));
          }
          line();

          section('Formas de pagamento');
          if (paymentEntries.length === 0) {
            row('Nenhum recebimento no periodo', '');
          } else {
            paymentEntries.forEach(([label, value]) => row(label, money(value)));
          }
          line();

          section('Comparativo mensal');
          row('Mes atual', money(currentMonthRevenue));
          row('Mes anterior', money(previousMonthRevenue));
          row('Variacao', monthVariation === null ? 'Sem base' : `${monthVariation >= 0 ? '+' : ''}${monthVariation.toFixed(1)}%`);

          addPageIfNeeded(20);
          y += 8;
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(8);
          doc.setTextColor(125, 125, 125);
          doc.text('* O lucro de produtos e estimado usando o custo atual cadastrado.', margin, y);
          y += 5;
          doc.text(`Emitido em ${new Date().toLocaleString('pt-BR')}`, margin, y);

          const periodFile =
            reportPeriod === 'today' ? 'Hoje' :
            reportPeriod === 'week' ? 'Semana' :
            reportPeriod === 'month'
              ? new Date().toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }).replace(' de ', '_')
              : 'Periodo_Completo';

          const safePeriod = periodFile
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/\s+/g, '_');

          doc.save(`Relatorio_Bion_${safePeriod}.pdf`);
        };

        return (
          <div className="screen-content">
            <div style={{display:'flex',justifyContent:'space-between',gap:'12px',alignItems:'end',flexWrap:'wrap',marginBottom:'18px'}}>
              <div>
                <div style={{fontSize:'13px',fontWeight:800,color:'#8b6f10',textTransform:'uppercase',letterSpacing:'1px'}}>Análise do negócio</div>
                <h2 style={{margin:'4px 0 5px',fontSize:'26px'}}>Relatórios</h2>
                <div style={{color:'#777',fontSize:'14px'}}>Financeiro, atendimentos e vendas em um só lugar.</div>
              </div>
              <div style={{display:'flex',gap:'10px',alignItems:'center',flexWrap:'wrap'}}>
                <select className="item-input" value={reportPeriod} onChange={e=>setReportPeriod(e.target.value)} style={{minWidth:'180px'}}>
                  <option value="today">Hoje</option>
                  <option value="week">Esta semana</option>
                  <option value="month">Este mês</option>
                  <option value="all">Todo o período</option>
                </select>
                <button
                  className="btn-primary"
                  onClick={downloadReportPDF}
                  style={{minHeight:'42px',padding:'0 18px',fontWeight:800,whiteSpace:'nowrap'}}
                >
                  Baixar PDF
                </button>
              </div>
            </div>

            <div style={{fontSize:'13px',fontWeight:800,color:'#777',marginBottom:'10px'}}>{periodLabel}</div>

            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(155px,1fr))',gap:'12px',marginBottom:'18px'}}>
              <div className="item-card" style={{display:'block',borderLeft:'5px solid #2e9d62'}}><div className="stat-label">Faturamento</div><div style={{fontSize:'24px',fontWeight:900,marginTop:'7px'}}>{money(revenue)}</div></div>
              <div className="item-card" style={{display:'block',borderLeft:'5px solid #c94b4b'}}><div className="stat-label">Despesas</div><div style={{fontSize:'24px',fontWeight:900,marginTop:'7px',color:'#b83232'}}>{money(expenseTotal)}</div></div>
              <div className="item-card" style={{display:'block',borderLeft:'5px solid #d4af37'}}><div className="stat-label">Saldo</div><div style={{fontSize:'24px',fontWeight:900,marginTop:'7px'}}>{money(balance)}</div></div>
              <div className="item-card" style={{display:'block'}}><div className="stat-label">Atendimentos concluídos</div><div style={{fontSize:'24px',fontWeight:900,marginTop:'7px'}}>{completed.length}</div></div>
              <div className="item-card" style={{display:'block'}}><div className="stat-label">Ticket médio de serviços</div><div style={{fontSize:'24px',fontWeight:900,marginTop:'7px'}}>{money(ticket)}</div></div>
              <div className="item-card" style={{display:'block'}}><div className="stat-label">Produtos vendidos</div><div style={{fontSize:'24px',fontWeight:900,marginTop:'7px'}}>{unitsSold}</div></div>
            </div>

            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))',gap:'14px',marginBottom:'18px'}}>
              <div className="item-card" style={{display:'block'}}>
                <div style={{fontSize:'18px',fontWeight:900,marginBottom:'14px'}}>💰 Receita por origem</div>
                <div style={{display:'grid',gap:'10px'}}>
                  <div style={{display:'flex',justifyContent:'space-between'}}><span>✂️ Serviços</span><strong>{money(serviceRevenue)}</strong></div>
                  <div style={{display:'flex',justifyContent:'space-between'}}><span>📦 Produtos</span><strong>{money(productRevenue)}</strong></div>
                  <div style={{display:'flex',justifyContent:'space-between',borderTop:'1px solid #eee',paddingTop:'10px'}}><span>Outras entradas</span><strong>{money(Math.max(0,revenue-serviceRevenue-productRevenue))}</strong></div>
                </div>
              </div>

              <div className="item-card" style={{display:'block'}}>
                <div style={{fontSize:'18px',fontWeight:900,marginBottom:'14px'}}>📦 Resultado de produtos</div>
                <div style={{display:'grid',gap:'10px'}}>
                  <div style={{display:'flex',justifyContent:'space-between'}}><span>Vendas registradas</span><strong>{money(productSalesValue)}</strong></div>
                  <div style={{display:'flex',justifyContent:'space-between'}}><span>Custo estimado</span><strong>{money(estimatedProductCost)}</strong></div>
                  <div style={{display:'flex',justifyContent:'space-between',borderTop:'1px solid #eee',paddingTop:'10px'}}><span>Lucro bruto estimado</span><strong style={{color:estimatedProductProfit>=0?'#247a4c':'#b83232'}}>{money(estimatedProductProfit)}</strong></div>
                </div>
              </div>
            </div>

            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))',gap:'14px'}}>
              <div className="item-card" style={{display:'block'}}>
                <div style={{fontSize:'18px',fontWeight:900,marginBottom:'12px'}}>✂️ Serviços mais realizados</div>
                {topServices.length===0 ? <div style={{color:'#777'}}>Nenhum atendimento concluído no período.</div> :
                  topServices.map(([name,count],idx)=><div key={name} style={{display:'flex',justifyContent:'space-between',padding:'9px 0',borderBottom:idx<topServices.length-1?'1px solid #eee':'none'}}><span>{idx+1}. {name}</span><strong>{count}</strong></div>)}
              </div>

              <div className="item-card" style={{display:'block'}}>
                <div style={{fontSize:'18px',fontWeight:900,marginBottom:'12px'}}>📦 Produtos mais vendidos</div>
                {topProducts.length===0 ? <div style={{color:'#777'}}>Nenhuma venda de produto no período.</div> :
                  topProducts.map(([name,count],idx)=><div key={name} style={{display:'flex',justifyContent:'space-between',padding:'9px 0',borderBottom:idx<topProducts.length-1?'1px solid #eee':'none'}}><span>{idx+1}. {name}</span><strong>{count} un</strong></div>)}
              </div>
            </div>

            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(300px,1fr))',gap:'14px',marginTop:'14px'}}>
              <div className="item-card" style={{display:'block'}}>
                <div style={{fontSize:'18px',fontWeight:900}}>📊 Faturamento diário</div>
                <div style={{fontSize:'12px',color:'#777'}}>Últimos 14 dias</div>
                <div style={{display:'flex',alignItems:'flex-end',gap:'7px',height:'190px',paddingTop:'18px',overflowX:'auto',borderBottom:'1px solid #ddd'}}>
                  {dailyRevenue.map(day=><div key={day.key} title={`${day.label}: ${money(day.value)}`} style={{minWidth:'34px',flex:1,height:'100%',display:'flex',flexDirection:'column',justifyContent:'flex-end',alignItems:'center'}}>
                    <div style={{fontSize:'9px',fontWeight:800,whiteSpace:'nowrap'}}>{day.value?money(day.value):''}</div>
                    <div style={{width:'70%',maxWidth:'36px',height:`${Math.max(3,(day.value/maxDailyRevenue)*125)}px`,background:'#d4af37',borderRadius:'6px 6px 0 0'}}></div>
                    <div style={{fontSize:'9px',color:'#777',marginTop:'4px'}}>{day.label}</div>
                  </div>)}
                </div>
              </div>
              <div className="item-card" style={{display:'block'}}>
                <div style={{fontSize:'18px',fontWeight:900,marginBottom:'10px'}}>💳 Formas de pagamento</div>
                {paymentEntries.length===0?<div style={{color:'#777'}}>Nenhum recebimento no período.</div>:paymentEntries.map(([label,value])=><div key={label} style={{display:'grid',gridTemplateColumns:'80px 1fr auto',gap:'10px',alignItems:'center',padding:'8px 0'}}>
                  <span style={{fontSize:'13px'}}>{label}</span>
                  <div style={{height:'10px',background:'#eee',borderRadius:'999px',overflow:'hidden'}}><div style={{height:'100%',width:`${(value/maxPayment)*100}%`,background:'#d4af37'}}></div></div>
                  <strong style={{fontSize:'13px'}}>{money(value)}</strong>
                </div>)}
              </div>
            </div>
            <div className="item-card" style={{display:'block',marginTop:'14px'}}>
              <div style={{fontSize:'18px',fontWeight:900,marginBottom:'12px'}}>📅 Comparativo mensal</div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))',gap:'12px'}}>
                <div><div className="stat-label">Mês atual</div><strong style={{fontSize:'21px'}}>{money(currentMonthRevenue)}</strong></div>
                <div><div className="stat-label">Mês anterior</div><strong style={{fontSize:'21px'}}>{money(previousMonthRevenue)}</strong></div>
                <div><div className="stat-label">Variação</div><strong style={{fontSize:'21px',color:monthVariation===null?'#777':monthVariation>=0?'#247a4c':'#b83232'}}>{monthVariation===null?'Sem base':`${monthVariation>=0?'+':''}${monthVariation.toFixed(1)}%`}</strong></div>
              </div>
            </div>
            <div style={{marginTop:'14px',fontSize:'12px',color:'#888'}}>
              * O lucro de produtos é estimado usando o custo atual cadastrado. O gráfico usa os lançamentos do Caixa.
            </div>
          </div>
        );
      })()}

      {/* Navigation */}
      <nav className="app-nav">
        <button className={`nav-btn ${screen === 'dashboard' ? 'active' : ''}`} onClick={() => setScreen('dashboard')}><span>⌂</span><b>Dashboard</b></button>
        <button className={`nav-btn ${screen === 'appointments' ? 'active' : ''}`} onClick={() => setScreen('appointments')}><span>▣</span><b>Agenda</b></button>
        <button className={`nav-btn ${screen === 'clients' ? 'active' : ''}`} onClick={() => setScreen('clients')}><span>♟</span><b>Clientes</b></button>
        <button className={`nav-btn ${screen === 'services' ? 'active' : ''}`} onClick={() => setScreen('services')}><span>✂</span><b>Serviços</b></button>
        <button className={`nav-btn ${screen === 'cash' ? 'active' : ''}`} onClick={() => setScreen('cash')}><span>◉</span><b>Caixa</b></button>
        <button className={`nav-btn ${screen === 'inventory' ? 'active' : ''}`} onClick={() => setScreen('inventory')}><span>◇</span><b>Estoque</b></button>
        <button className={`nav-btn ${screen === 'reports' ? 'active' : ''}`} onClick={() => setScreen('reports')}><span>↗</span><b>Relatórios</b></button>
      </nav>
    </div>
  );
}

export default App;
